import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, formatApiError } from "@/lib/api";
import { formatItemDisplay } from "@/lib/orderItem";
import AdminOrdersCommandCenter from "@/components/AdminOrdersCommandCenter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminContactSubmissions from "@/components/AdminContactSubmissions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Sparkles, Search, Download, Mail, Upload, X, Printer, Truck, Users, Merge, FileCheck2, FileWarning, Eye, FileText, ChevronLeft } from "lucide-react";
import ProductImageUploader from "@/components/ProductImageUploader";
import SalesSnapshot from "@/components/SalesSnapshot";
import SalesChart from "@/components/SalesChart";
import BulkImportDialog from "@/components/BulkImportDialog";
import VariantsBulkImportDialog from "@/components/VariantsBulkImportDialog";
import DuplicateMergeDialog from "@/components/DuplicateMergeDialog";
import ManualInvoiceDialog from "@/components/ManualInvoiceDialog";
import EditOrderDialog from "@/components/EditOrderDialog";
import AdminAccessRequests from "@/components/AdminAccessRequests";
import AdminInventory from "@/components/AdminInventory";
import AdminInventory10VK from "@/components/AdminInventory10VK";
import AdminPurchaseOrders from "@/components/AdminPurchaseOrders";
import AdminFulfillment from "@/components/AdminFulfillment";
import AdminSidebar from "@/components/AdminSidebar";
import AdminSuppliersDirectory from "@/components/AdminSuppliersDirectory";
import SlugRedirects from "@/components/SlugRedirects";
import EventOutboxNudge from "@/components/EventOutboxNudge";
import AdminUsername from "@/components/AdminUsername";
import TempAccessPanel from "@/components/TempAccessPanel";
import ShippingOriginPanel from "@/components/ShippingOriginPanel";
import CoaDropzone from "@/components/CoaDropzone";

const emptyProduct = { name: "", slug: "", sku: "", category: "", unit_label: "3 vials/kit", price: 0, price_10vial: null, price_3vial: null, price_5vial: null, variants: [], variant_unit: "mg", dose_mg: "", description: "", spec: [], is_blend: false, components: [], image_url: "", hero_image_url: "", stock: 100, active: true, in_stock: true, featured: false, tier: "", is_lyo_usa: false, is_hot_seller: false, max_qty_per_order: null, single_buy: false, byok_eligible: false, admin_notes: "", ignore_supplier_sync: false, select_ladder_override_3vial: null, select_ladder_override_5vial: null, vials_in_stock: null, supply_tier_id: null, kit_vial_count: 3, kit_vial_strength_mg: "", related_product_ids: [] };
const emptyTrial = { name: "", email: "", days: 3 };
const emptyCode = { code: "", discount_type: "percent", discount_value: 10, usage_limit: "", expires_at: "", active: true, tier: "" };

export default function Admin() {
  // Iter155 — honor `?tab=X` deep-links on mount + sync back on tab change
  // so shareable admin URLs (e.g., /admin?tab=inventory) land on the right
  // panel. Falls back to "orders" for missing/invalid tab values.
  const _initialTab = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get("tab");
      const allowed = new Set(["orders","products","trials","codes","compliance","customers","banner","snippets","mailing","settings","access","supplier","inventory","purchase-orders","redirects","fulfillment","messages","suppliers-directory","reports"]);
      if (q && allowed.has(q)) return q;
    } catch (_) {}
    // iter184 Phase 1 — quiet Admin landing. No module opens automatically.
    // Deep links via ?tab=xxx still resolve above.
    return "";
  })();
  const [tab, setTab] = useState(_initialTab);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trials, setTrials] = useState([]);
  const [codes, setCodes] = useState([]);
  const [complianceRecords, setComplianceRecords] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [subs, setSubs] = useState({ active_count: 0, unsubscribed_count: 0, subscribers: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [shipCfg, setShipCfg] = useState({ flat_rate: 22, flat_rate_select: 16, free_threshold: 250, free_threshold_select: 250, min_kits_supply: 1, min_kits_select: 1 });
  const [shipDraft, setShipDraft] = useState({ flat_rate: "22", flat_rate_select: "16", free_threshold: "250", free_threshold_select: "250" });
  const [shipBusy, setShipBusy] = useState(false);
  // BYOK discount ladder (admin-tunable). Stored as fractions (0.10 = 10% off);
  // rendered / edited as percent for the human.
  const [byokCfg, setByokCfg] = useState({ discount_3vial: 0.10, discount_5vial: 0.20 });
  const [byokDraft, setByokDraft] = useState({ discount_3vial: "10", discount_5vial: "20" });
  const [byokBusy, setByokBusy] = useState(false);

  // Select-tier vial-quantity ladder (single-product discount at 3/5 vials).
  // Same shape / storage convention as BYOK ladder.
  const [selectLadderCfg, setSelectLadderCfg] = useState({ discount_3vial: 0.05, discount_5vial: 0.10, discount_10vial: 0 });
  const [selectLadderDraft, setSelectLadderDraft] = useState({ discount_3vial: "5", discount_5vial: "10", discount_10vial: "0" });
  const [selectLadderBusy, setSelectLadderBusy] = useState(false);
  const [contactUnread, setContactUnread] = useState(0);
  // Phase Purdy hardening (iter190) — surface partial-load failures so the
  // Admin never falsely reports every dataset as empty. Null = clean load.
  const [loadWarning, setLoadWarning] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/byok");
        setByokCfg(data);
        setByokDraft({
          discount_3vial: String(Math.round((data.discount_3vial || 0) * 1000) / 10),
          discount_5vial: String(Math.round((data.discount_5vial || 0) * 1000) / 10),
        });
      } catch {/* defaults stay */}
      try {
        const { data } = await api.get("/admin/settings/select-ladder");
        setSelectLadderCfg(data);
        setSelectLadderDraft({
          discount_3vial: String(Math.round((data.discount_3vial || 0) * 1000) / 10),
          discount_5vial: String(Math.round((data.discount_5vial || 0) * 1000) / 10),
          discount_10vial: String(Math.round((data.discount_10vial || 0) * 1000) / 10),
        });
      } catch {/* defaults stay */}
    })();
  }, []);

  const saveByokSettings = async (e) => {
    e.preventDefault();
    const p3 = parseFloat(byokDraft.discount_3vial);
    const p5 = parseFloat(byokDraft.discount_5vial);
    if (Number.isNaN(p3) || p3 < 0 || p3 > 90) { toast.error("3-vial discount must be 0–90%"); return; }
    if (Number.isNaN(p5) || p5 < 0 || p5 > 90) { toast.error("5-vial discount must be 0–90%"); return; }
    setByokBusy(true);
    try {
      const { data } = await api.put("/admin/settings/byok", {
        discount_3vial: p3 / 100,
        discount_5vial: p5 / 100,
      });
      setByokCfg(data);
      setByokDraft({
        discount_3vial: String(Math.round(data.discount_3vial * 1000) / 10),
        discount_5vial: String(Math.round(data.discount_5vial * 1000) / 10),
      });
      toast.success("BYOK discount saved — applies to new kits immediately.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setByokBusy(false);
    }
  };

  const saveSelectLadderSettings = async (e) => {
    e.preventDefault();
    const p3 = parseFloat(selectLadderDraft.discount_3vial);
    const p5 = parseFloat(selectLadderDraft.discount_5vial);
    const p10 = parseFloat(selectLadderDraft.discount_10vial);
    if (Number.isNaN(p3) || p3 < 0 || p3 > 90) { toast.error("3-vial discount must be 0–90%"); return; }
    if (Number.isNaN(p5) || p5 < 0 || p5 > 90) { toast.error("5-vial discount must be 0–90%"); return; }
    if (Number.isNaN(p10) || p10 < 0 || p10 > 90) { toast.error("10-vial discount must be 0–90%"); return; }
    setSelectLadderBusy(true);
    try {
      const { data } = await api.put("/admin/settings/select-ladder", {
        discount_3vial: p3 / 100,
        discount_5vial: p5 / 100,
        discount_10vial: p10 / 100,
      });
      setSelectLadderCfg(data);
      setSelectLadderDraft({
        discount_3vial: String(Math.round(data.discount_3vial * 1000) / 10),
        discount_5vial: String(Math.round(data.discount_5vial * 1000) / 10),
        discount_10vial: String(Math.round((data.discount_10vial || 0) * 1000) / 10),
      });
      toast.success("Select ladder saved — applies immediately.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSelectLadderBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/shipping");
        setShipCfg(data);
        setShipDraft({
          flat_rate: String(data.flat_rate ?? 22),
          flat_rate_select: String(data.flat_rate_select ?? 16),
          free_threshold: String(data.free_threshold ?? 250),
          free_threshold_select: String(data.free_threshold_select ?? 250),
        });
      } catch {/* fine, defaults stay */}
    })();
  }, []);

  const saveShippingSettings = async (e) => {
    e.preventDefault();
    const threshSupply = parseFloat(shipDraft.free_threshold);
    const threshSelect = parseFloat(shipDraft.free_threshold_select);
    if (Number.isNaN(threshSupply) || threshSupply < 0) { toast.error("SUPPLY free-ship threshold must be a positive number"); return; }
    if (Number.isNaN(threshSelect) || threshSelect < 0) { toast.error("SELECT free-ship threshold must be a positive number"); return; }
    setShipBusy(true);
    try {
      // Preserve ALL other fields (flat rates + min-kits) from the loaded
      // config so a Save that only edits the thresholds does not clobber
      // adjacent settings. The backend PUT replaces every field in the doc.
      const { data } = await api.put("/admin/settings/shipping", {
        flat_rate: shipCfg.flat_rate,
        flat_rate_select: shipCfg.flat_rate_select,
        free_threshold: threshSupply,
        free_threshold_select: threshSelect,
        min_kits_supply: shipCfg.min_kits_supply,
        min_kits_select: shipCfg.min_kits_select,
      });
      setShipCfg(data);
      setShipDraft({
        flat_rate: String(data.flat_rate ?? 22),
        flat_rate_select: String(data.flat_rate_select ?? 16),
        free_threshold: String(data.free_threshold ?? 250),
        free_threshold_select: String(data.free_threshold_select ?? 250),
      });
      toast.success("Shipping settings saved — applies to new orders immediately.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setShipBusy(false);
    }
  };
  const [orderSearch, setOrderSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [variantsImportOpen, setVariantsImportOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);
  // Full order view modal (iter120 — read-only detail popup for the admin).
  const [viewingOrder, setViewingOrder] = useState(null);
  // Full-field edit dialog (iter131 — admin can rewrite any field on an order).
  const [editingOrder, setEditingOrder] = useState(null);
  // iter_133 — minimize support: refs stash the in-progress form snapshot so
  // an admin can dismiss either dialog, keep working elsewhere in the console,
  // and come back to exactly where they left off. The chip below the console
  // is the only re-entry point while minimized.
  const manualDraftRef = useRef(null);
  const editDraftRef = useRef(null);
  const [manualMinimized, setManualMinimized] = useState(false);
  const [editMinimized, setEditMinimized] = useState(false);
  const [minimizedEditOrder, setMinimizedEditOrder] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  // iter216 — 10VK minimal-form flag. True ONLY when the New Product dialog
  // was opened from the 10VK sub-tab. Collapses the form to Name / Price /
  // Image / Kits In Stock. Reset to false on every other open path.
  const [is10vkNew, setIs10vkNew] = useState(false);
  // 10VK PEC — IMS Connection. Eligible lots = orphaned (pec_deleted) or
  // already linked to the product being edited. Linking writes only
  // product_id / pec_deleted / updated_at on the lot (never quantities).
  const [tenvkLots, setTenvkLots] = useState([]);
  const [tenvkLinked, setTenvkLinked] = useState(new Set());
  const [tenvkInitialLinked, setTenvkInitialLinked] = useState(new Set());
  const [tenvkBusy, setTenvkBusy] = useState(false);
  const loadTenvkLots = async (productId) => {
    try {
      const { data } = await api.get("/admin/inventory/10vk/lots");
      const rows = (data?.rows || []).filter((r) => r.pec_deleted || (productId && r.product_id === productId));
      const linked = new Set(rows.filter((r) => !r.pec_deleted && r.product_id === productId).map((r) => r.lot_id));
      setTenvkLots(rows);
      setTenvkLinked(linked);
      setTenvkInitialLinked(new Set(linked));
    } catch {
      setTenvkLots([]);
      setTenvkLinked(new Set());
      setTenvkInitialLinked(new Set());
      toast.error("Couldn't load 10VK IMS lots");
    }
  };
  // iter184 — Supply price tiers loaded once at admin bootstrap. Used to
  // render the "Supply Tier" dropdown on Supply-tier products.
  const [supplyTiers, setSupplyTiers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Auto-save draft (localStorage) — recovers unsaved edits if the browser
  // crashes or the tab reloads while the product form is open.
  const [restoreDraft, setRestoreDraft] = useState(null); // { data, savedAt } | null
  const initialFormRef = useRef(null); // JSON snapshot of form at dialog open (to skip no-op writes)
  const draftKey = (id) => `bbk-product-draft-${id || "new"}`;
  const captureDraftOnOpen = (id, product) => {
    try {
      const raw = localStorage.getItem(draftKey(id));
      if (!raw) return null;
      const draft = JSON.parse(raw);
      if (!draft || !draft.data || !draft.savedAt) return null;
      // Editing an existing product: only surface the draft if it's newer than
      // what the server currently holds (otherwise the server version wins).
      if (id && product?.updated_at) {
        const productMs = new Date(product.updated_at).getTime();
        const draftMs = new Date(draft.savedAt).getTime();
        if (!isFinite(draftMs) || draftMs <= productMs) return null;
      }
      return draft;
    } catch { return null; }
  };
  const clearDraft = (id) => {
    try { localStorage.removeItem(draftKey(id)); } catch {}
  };
  const restoreDraftNow = () => {
    if (!restoreDraft?.data) return;
    setProductForm(restoreDraft.data);
    setPricingModel(inferPricingModel(restoreDraft.data));
    initialFormRef.current = JSON.stringify(restoreDraft.data);
    setRestoreDraft(null);
    toast.success("Draft restored");
  };
  const discardDraftNow = () => {
    clearDraft(editingId);
    setRestoreDraft(null);
    toast.success("Draft discarded");
  };
  const [genBusy, setGenBusy] = useState(false);
  const [adminNotesOpen, setAdminNotesOpen] = useState(false);
  // Pricing model: "single-price" (Supply flat), "single-vial" (Select — unit
  // price; ladder handles 3v/5v tile prices automatically), "multi-mg"
  // (mg-per-vial dropdown, either tier — Select mgs also get ladder-priced
  // 3v/5v tiles). Legacy "multi-vial-kit" and "multi-mg-kit" modes were
  // retired in 2026-02 (the Select ladder in Admin → Settings replaces them).
  const [pricingModel, setPricingModel] = useState("single-price");
  const inferPricingModel = (f) => {
    const tier = f?.tier || "supply";
    const hasVariants = Array.isArray(f?.variants) && f.variants.length > 0;
    if (hasVariants) return "multi-mg";
    return tier === "select" ? "single-vial" : "single-price";
  };
  const setPricingModelAndClean = (nextModel) => {
    setPricingModel(nextModel);
    setProductForm((f) => {
      const next = { ...f };
      // Always drop legacy 3v/5v fields — the Select ladder handles them now.
      next.price_3vial = null;
      next.price_5vial = null;
      if (nextModel === "single-price" || nextModel === "single-vial") {
        next.variants = [];
      } else if (nextModel === "multi-mg") {
        // Strip any legacy per-variant kit prices.
        next.variants = (Array.isArray(next.variants) ? next.variants : []).map((v) => {
          const { price_3vial, price_5vial, ...rest } = v || {};
          return rest;
        });
      }
      return next;
    });
  };
  const [trialForm, setTrialForm] = useState(emptyTrial);
  const [newTrial, setNewTrial] = useState(null);
  const [codeForm, setCodeForm] = useState(emptyCode);

  const generateDescription = async () => {
    if (!productForm.name?.trim()) {
      toast.error("Enter a product name first");
      return;
    }
    setGenBusy(true);
    try {
      const { data } = await api.post("/admin/products/generate-description", {
        name: productForm.name,
        dose_mg: productForm.dose_mg,
        category: productForm.category,
      });
      setProductForm((f) => ({ ...f, description: data.description, spec: data.spec || [] }));
      toast.success("Description generated — review before saving");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally {
      setGenBusy(false);
    }
  };

  const load = async () => {
    // Phase Purdy hardening (iter190) — one bad endpoint must not zero out
    // every other Admin tab. Promise.allSettled lets successful sections
    // populate; failures are kept out of state (previous values preserved)
    // and surfaced as a single restrained warning banner.
    const requests = [
      { name: "orders",             url: "/admin/orders",             apply: (d) => setOrders(d) },
      { name: "products",           url: "/admin/products",           apply: (d) => setProducts(d) },
      { name: "categories",         url: "/admin/categories",         apply: (d) => setCategories(d) },
      { name: "trials",             url: "/admin/trials",             apply: (d) => setTrials(d) },
      { name: "discount-codes",     url: "/admin/discount-codes",     apply: (d) => setCodes(d) },
      { name: "compliance-records", url: "/admin/compliance-records", apply: (d) => setComplianceRecords(d) },
      { name: "dashboard",          url: "/admin/dashboard",          apply: (d) => setDashboard(d) },
      { name: "customers",          url: "/admin/customers",          apply: (d) => setCustomers(d) },
      { name: "banners",            url: "/admin/banners",            apply: (d) => setBanners(d) },
      { name: "note-snippets",      url: "/admin/note-snippets",      apply: (d) => setSnippets(d) },
      { name: "subscribers",        url: "/admin/subscribers",        apply: (d) => setSubs(d) },
      { name: "campaigns",          url: "/admin/campaigns",          apply: (d) => setCampaigns(d) },
      { name: "oos-waitlist",       url: "/admin/oos-waitlist",       apply: (d) => {
          const counts = {};
          for (const e of d || []) counts[e.product_id] = (counts[e.product_id] || 0) + 1;
          setWaitlistCounts(counts);
        } },
      { name: "supply/tiers",       url: "/admin/supply/tiers",       apply: (d) => setSupplyTiers(d) },
    ];
    const results = await Promise.allSettled(requests.map((r) => api.get(r.url)));
    const failed = [];
    results.forEach((res, i) => {
      const req = requests[i];
      if (res.status === "fulfilled") {
        try { req.apply(res.value.data); } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[bbk.admin.load] apply(${req.name}) threw:`, e);
          failed.push(`${req.name} (apply-error)`);
        }
      } else {
        const status = res.reason?.response?.status ?? "network";
        failed.push(`${req.name} (${status})`);
      }
    });
    if (failed.length) {
      // eslint-disable-next-line no-console
      console.warn("[bbk.admin.load] Some admin datasets failed to load:", failed);
      setLoadWarning(`Some Admin data failed to load. Refresh or sign in again.`);
    } else {
      setLoadWarning(null);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredOrders = orders.filter((o) => {
    const n = orderSearch.trim().toLowerCase();
    if (!n) return true;
    return (
      (o.order_number || "").toLowerCase().includes(n) ||
      (o.customer_email || "").toLowerCase().includes(n) ||
      (o.customer_name || "").toLowerCase().includes(n)
    );
  });

  // ─── Products are ALWAYS displayed alphabetical. No drag-to-reorder. ──────
  // Defensive belt-and-suspenders: even if the API somehow returns unsorted, the
  // display forces case-insensitive AND natural-numeric name ordering
  // ("RT-2A 5mg" sorts BEFORE "RT-2A 10mg", not after "RT-2A 30mg").
  const [productTierTab, setProductTierTab] = useState("supply"); // "supply" | "select" | "10vk"
  const sortedProducts = useMemo(
    () => {
      const base = [...products];
      // iter216 — 10VK sub-tab: kit_vial_count === 10 AND testing_status === "untested".
      // SUPPLY / SELECT behavior unchanged.
      const filtered = productTierTab === "10vk"
        ? base.filter((p) => p.kit_vial_count === 10)
        : base.filter((p) => (p.tier || "supply") === productTierTab);
      return filtered.sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }));
    },
    [products, productTierTab]
  );
  const supplyCount = useMemo(() => products.filter((p) => (p.tier || "supply") === "supply").length, [products]);
  const selectCount = useMemo(() => products.filter((p) => p.tier === "select").length, [products]);
  const tenVKCount = useMemo(() => products.filter((p) => p.kit_vial_count === 10).length, [products]);

  // ─── Customers ──────────────────────────────────────────────────────────────
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [tagInputFor, setTagInputFor] = useState(null);
  const [tagDraft, setTagDraft] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [customerBusyId, setCustomerBusyId] = useState(null);
  const [confirmDeleteCustomer, setConfirmDeleteCustomer] = useState(null);
  const [waitlistCounts, setWaitlistCounts] = useState({});
  const [waitlistProduct, setWaitlistProduct] = useState(null);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const openWaitlist = async (p) => {
    setWaitlistProduct(p);
    setWaitlistEntries([]);
    setWaitlistLoading(true);
    try {
      const { data } = await api.get(`/admin/products/${p.id}/waitlist`);
      setWaitlistEntries(data);
    } catch {
      toast.error("Couldn't load waitlist");
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Track products with an in-flight temp-visible toggle so we can disable the
  // button while the round-trip is in flight. Optimistic UI updates the local
  // list immediately; a failure rolls it back and toasts.
  const [tempVisibleBusy, setTempVisibleBusy] = useState(new Set());
  const toggleTempVisible = async (p) => {
    if (tempVisibleBusy.has(p.id)) return;
    const next = !p.temp_visible;
    // Optimistic update on the parent list state — `products` is set by load()
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, temp_visible: next } : x)));
    setTempVisibleBusy((prev) => new Set(prev).add(p.id));
    try {
      await api.patch(`/admin/products/${p.id}/temp-visible`, { visible: next });
      toast.success(next ? `Temp login can now see ${p.name}.` : `Removed ${p.name} from temp preview.`);
    } catch (e) {
      // Rollback on failure
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, temp_visible: !next } : x)));
      toast.error(formatApiError(e, "Couldn't update temp visibility."));
    } finally {
      setTempVisibleBusy((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
    }
  };
  const [landingCfg, setLandingCfg] = useState({ consultation_phone: "931-327-0458", select_now_live_enabled: false });
  const [landingDraft, setLandingDraft] = useState("931-327-0458");
  const [landingBusy, setLandingBusy] = useState(false);
  const [selectNowLiveBusy, setSelectNowLiveBusy] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupBusy, setSignupBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/landing");
        setLandingCfg(data);
        setLandingDraft(data.consultation_phone || "");
      } catch {/* defaults fine */}
      try {
        const { data } = await api.get("/admin/settings/signup");
        setSignupOpen(Boolean(data.public_signup_enabled));
      } catch {/* defaults fine */}
    })();
  }, []);

  const saveLandingSettings = async (e) => {
    e.preventDefault();
    const phone = landingDraft.trim();
    if (!phone) { toast.error("Phone number is required"); return; }
    setLandingBusy(true);
    try {
      const { data } = await api.put("/admin/settings/landing", { consultation_phone: phone });
      setLandingCfg(data);
      setLandingDraft(data.consultation_phone);
      toast.success("Landing page phone updated — live now.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLandingBusy(false);
    }
  };

  const toggleSelectNowLive = async (next) => {
    setSelectNowLiveBusy(true);
    try {
      const { data } = await api.put("/admin/settings/landing", {
        consultation_phone: landingCfg.consultation_phone || landingDraft || "931-327-0458",
        select_now_live_enabled: next,
      });
      setLandingCfg(data);
      toast.success(next ? "'Now Live' ribbon is showing on the Select catalog." : "'Now Live' ribbon hidden.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSelectNowLiveBusy(false);
    }
  };

  const toggleSignup = async (next) => {
    setSignupBusy(true);
    try {
      const { data } = await api.put("/admin/settings/signup", { public_signup_enabled: next });
      setSignupOpen(Boolean(data.public_signup_enabled));
      toast.success(next ? "Public signup is now OPEN." : "Public signup is now CLOSED.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSignupBusy(false);
    }
  };

  const deleteCustomer = async (c) => {
    setCustomerBusyId(c.id);
    try {
      await api.delete(`/admin/customers/${c.id}`);
      setCustomers((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`Deleted ${c.email}. Their order history is preserved.`);
      setConfirmDeleteCustomer(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCustomerBusyId(null);
    }
  };

  const filteredCustomers = customers.filter((c) => {
    const n = customerSearch.trim().toLowerCase();
    if (tagFilter && !(c.tags || []).some((t) => t.toLowerCase() === tagFilter.toLowerCase())) {
      return false;
    }
    if (!n) return true;
    return (
      (c.email || "").toLowerCase().includes(n) ||
      (c.name || "").toLowerCase().includes(n)
    );
  });
  const allTags = Array.from(new Set(customers.flatMap((c) => c.tags || []))).sort();

  const saveTags = async (c, nextTags) => {
    try {
      const { data } = await api.patch(`/admin/customers/${c.id}/tags`, { tags: nextTags });
      setCustomers((prev) => prev.map((x) => (x.id === c.id ? { ...x, tags: data.tags } : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const addTagTo = async (c) => {
    const t = tagDraft.trim();
    if (!t) return;
    const next = [...(c.tags || []), t];
    setTagDraft("");
    setTagInputFor(null);
    await saveTags(c, next);
  };
  const removeTag = async (c, tag) => {
    const next = (c.tags || []).filter((t) => t !== tag);
    await saveTags(c, next);
  };

  const openNoteEditor = (c) => {
    setEditingNoteId(c.id);
    setNoteDraft(c.admin_notes || "");
  };
  const cancelNoteEditor = () => { setEditingNoteId(null); setNoteDraft(""); };
  const saveNote = async (c) => {
    try {
      await api.patch(`/admin/customers/${c.id}/notes`, { admin_notes: noteDraft });
      // Update inline
      setCustomers((prev) => prev.map((x) => (x.id === c.id ? { ...x, admin_notes: noteDraft } : x)));
      toast.success(noteDraft ? "Note saved" : "Note cleared");
      cancelNoteEditor();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ─── Banners ────────────────────────────────────────────────────────────────
  const [bannerForm, setBannerForm] = useState({ message: "", link_url: "", link_label: "", enabled: true, marquee: false, speed: "medium", tier: "" });
  const [editingBannerId, setEditingBannerId] = useState(null);

  const openNewBanner = () => {
    setBannerForm({ message: "", link_url: "", link_label: "", enabled: true, marquee: false, speed: "medium", tier: "" });
    setEditingBannerId(null);
  };
  const openEditBanner = (b) => {
    setBannerForm({
      message: b.message,
      link_url: b.link_url || "",
      link_label: b.link_label || "",
      enabled: b.enabled,
      marquee: !!b.marquee,
      speed: b.speed || "medium",
      tier: b.tier || "",
    });
    setEditingBannerId(b.id);
  };
  const saveBanner = async (e) => {
    e.preventDefault();
    try {
      if (editingBannerId) {
        await api.patch(`/admin/banners/${editingBannerId}`, bannerForm);
        toast.success("Banner updated");
      } else {
        await api.post("/admin/banners", bannerForm);
        toast.success("Banner created");
      }
      openNewBanner();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteBanner = async (id) => {
    if (!confirm("Delete this banner?")) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success("Banner deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ─── Weekly digest send-now ─────────────────────────────────────────────────
  const sendWeeklyDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/send-weekly-digest");
      toast.success(`Weekly digest sent to ${data.sent} admin${data.sent === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDigestBusy(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteOrder = async (id, orderNumber) => {
    if (!confirm(`Delete order #${orderNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Order deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Order notes to buyer
  const [noteOrderId, setNoteOrderId] = useState(null);
  const [noteOrderDraft, setNoteOrderDraft] = useState("");
  const openOrderNote = (o) => { setNoteOrderId(o.id); setNoteOrderDraft(o.note_to_buyer || ""); };
  const closeOrderNote = () => { setNoteOrderId(null); setNoteOrderDraft(""); };
  const saveOrderNote = async () => {
    try {
      await api.patch(`/admin/orders/${noteOrderId}/note`, { note_to_buyer: noteOrderDraft });
      setOrders((prev) => prev.map((o) => (o.id === noteOrderId ? { ...o, note_to_buyer: noteOrderDraft } : o)));
      toast.success(noteOrderDraft.trim() ? "Note saved — buyers see it on their slip + emails." : "Note cleared");
      closeOrderNote();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const insertSnippet = (body) => {
    setNoteOrderDraft((prev) => (prev.trim() ? `${prev.trim()}\n${body}` : body));
  };

  // Tracking dialog
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [trackingDraft, setTrackingDraft] = useState({ carrier: "", number: "" });
  const [refundOrderId, setRefundOrderId] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);

  const openRefund = (o) => {
    setRefundOrderId(o.id);
    const remaining = (o.total - (o.refunded_amount || 0)).toFixed(2);
    setRefundAmount(remaining);
  };
  const closeRefund = () => { setRefundOrderId(null); setRefundAmount(""); };

  const submitRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (Number.isNaN(amount) || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setRefundBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${refundOrderId}/refund`, { amount });
      setOrders((prev) => prev.map((x) => (x.id === refundOrderId ? {
        ...x,
        status: data.fully_refunded ? "cancelled" : x.status,
        refunded_amount: data.refunded_total,
      } : x)));
      toast.success(`Refunded $${data.amount.toFixed(2)} — buyer emailed.`);
      closeRefund();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRefundBusy(false);
    }
  };

  const openTracking = (o) => {
    setTrackingOrderId(o.id);
    setTrackingDraft({ carrier: o.tracking_carrier || "", number: o.tracking_number || "" });
  };
  const closeTracking = () => { setTrackingOrderId(null); setTrackingDraft({ carrier: "", number: "" }); };
  const saveTracking = async () => {
    try {
      const { data } = await api.patch(`/admin/orders/${trackingOrderId}/tracking`, {
        tracking_carrier: trackingDraft.carrier,
        tracking_number: trackingDraft.number,
      });
      setOrders((prev) => prev.map((o) => (o.id === trackingOrderId
        ? { ...o, tracking_carrier: trackingDraft.carrier, tracking_number: trackingDraft.number }
        : o)));
      if (trackingDraft.number.trim()) {
        toast.success(data.email_sent ? "Tracking saved — email sent to buyer." : "Tracking saved.");
      } else {
        toast.success("Tracking cleared.");
      }
      closeTracking();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Note Snippets (admin-editable templates)
  const [snippetForm, setSnippetForm] = useState({ title: "", body: "" });
  const [editingSnippetId, setEditingSnippetId] = useState(null);
  const resetSnippetForm = () => { setSnippetForm({ title: "", body: "" }); setEditingSnippetId(null); };
  const openEditSnippet = (s) => { setSnippetForm({ title: s.title, body: s.body }); setEditingSnippetId(s.id); };
  const saveSnippet = async (e) => {
    e.preventDefault();
    if (!snippetForm.title.trim() || !snippetForm.body.trim()) {
      toast.error("Title and body required");
      return;
    }
    try {
      if (editingSnippetId) {
        const { data } = await api.patch(`/admin/note-snippets/${editingSnippetId}`, snippetForm);
        setSnippets((prev) => prev.map((s) => (s.id === editingSnippetId ? data : s)));
        toast.success("Template updated");
      } else {
        const { data } = await api.post("/admin/note-snippets", snippetForm);
        setSnippets((prev) => [...prev, data]);
        toast.success("Template added");
      }
      resetSnippetForm();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteSnippet = async (id) => {
    if (!confirm("Delete this template?")) return;
    try {
      await api.delete(`/admin/note-snippets/${id}`);
      setSnippets((prev) => prev.filter((s) => s.id !== id));
      if (editingSnippetId === id) resetSnippetForm();
      toast.success("Template deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Campaign composer
  const [campaignForm, setCampaignForm] = useState({ subject: "", body_html: "", segment_tags: [] });
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [campaignBusy, setCampaignBusy] = useState(false);
  const [showSent, setShowSent] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/campaigns/templates");
        setTemplates(data);
      } catch {/* fine, admin can still hand-write */}
    })();
  }, []);

  const loadTemplate = async (name) => {
    if (!name) return;
    setTemplateLoading(true);
    try {
      const { data } = await api.get(`/admin/campaigns/render-template?name=${encodeURIComponent(name)}`);
      setCampaignForm((f) => ({ ...f, subject: data.subject, body_html: data.body_html }));
      toast.success("Template loaded — edit freely, then Create Draft to save.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTemplateLoading(false);
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({ subject: "", body_html: "", segment_tags: [] });
    setEditingCampaignId(null);
  };
  const openEditCampaign = (c) => {
    setCampaignForm({ subject: c.subject, body_html: c.body_html, segment_tags: c.segment_tags || [] });
    setEditingCampaignId(c.id);
  };
  const saveCampaign = async (e) => {
    e.preventDefault();
    if (!campaignForm.subject.trim() || !campaignForm.body_html.trim()) {
      toast.error("Subject and body required");
      return;
    }
    setCampaignBusy(true);
    try {
      let saved;
      if (editingCampaignId) {
        const { data } = await api.patch(`/admin/campaigns/${editingCampaignId}`, campaignForm);
        saved = data;
        setCampaigns((prev) => prev.map((c) => (c.id === editingCampaignId ? data : c)));
        toast.success("Campaign draft saved");
      } else {
        const { data } = await api.post("/admin/campaigns", campaignForm);
        saved = data;
        setCampaigns((prev) => [data, ...prev]);
        setEditingCampaignId(data.id);
        toast.success("Draft created — test-send it to yourself before going live.");
      }
      // Keep the form on the just-saved campaign so admin can test-send it
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCampaignBusy(false);
    }
  };
  const testSendCampaign = async () => {
    if (!editingCampaignId) {
      toast.error("Save the draft first, then test-send.");
      return;
    }
    setCampaignBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${editingCampaignId}/test-send`);
      setCampaigns((prev) => prev.map((c) => (c.id === editingCampaignId ? { ...c, test_sent_at: data.test_sent_at } : c)));
      toast.success(`Test sent to ${data.sent_to}. Check your inbox before sending live.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCampaignBusy(false);
    }
  };
  const sendCampaign = async () => {
    if (!editingCampaignId) return;
    const current = campaigns.find((c) => c.id === editingCampaignId);
    if (!current?.test_sent_at) {
      toast.error("Please test-send to yourself first.");
      return;
    }
    if (!confirm(`Send "${campaignForm.subject}" to ${subs.active_count} subscriber${subs.active_count === 1 ? "" : "s"}? This can't be undone.`)) return;
    setCampaignBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${editingCampaignId}/send`);
      toast.success(`Sent to ${data.sent} of ${data.attempted}.`);
      await load();
      resetCampaignForm();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCampaignBusy(false);
    }
  };
  const deleteCampaign = async (id) => {
    if (!confirm("Delete this draft?")) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      if (editingCampaignId === id) resetCampaignForm();
      toast.success("Draft deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const toggleSegmentTag = (t) => {
    setCampaignForm((f) => ({
      ...f,
      segment_tags: f.segment_tags.includes(t) ? f.segment_tags.filter((x) => x !== t) : [...f.segment_tags, t],
    }));
  };

  const deleteComplianceRecord = async (r) => {
    if (!confirm(`Remove compliance record for ${r.email}? The user account stays; only the signed acknowledgment is cleared. They'll be asked to re-sign on next login.`)) return;
    try {
      await api.delete(`/admin/compliance-records/${r.id}`);
      setComplianceRecords((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Compliance record cleared.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openNew = () => {
    const isTenVK = productTierTab === "10vk";
    const fresh = {
      ...emptyProduct,
      tier: isTenVK ? "10vk" : productTierTab,
      // Sensible default kit label per tier — admin can still override.
      unit_label: isTenVK ? "10-vial Kit" : (productTierTab === "select" ? "3 or 5 vial kit" : "3 vials/kit"),
      // iter216 — auto-assigned by the 10VK sub-tab; operator never touches them.
      kit_vial_count: isTenVK ? 10 : (emptyProduct.kit_vial_count ?? 3),
      testing_status: isTenVK ? "untested" : emptyProduct.testing_status,
      kits_in_stock: isTenVK ? 0 : null,
      category: isTenVK ? "peptide" : emptyProduct.category,
    };
    setProductForm(fresh);
    setPricingModel(inferPricingModel(fresh));
    setEditingId(null);
    setSlugTouched(false);
    setIs10vkNew(isTenVK);
    if (isTenVK) loadTenvkLots(null);
    initialFormRef.current = JSON.stringify(fresh);
    setRestoreDraft(captureDraftOnOpen(null, null));
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setProductForm({ ...p });
    setPricingModel(inferPricingModel(p));
    setEditingId(p.id);
    setSlugTouched(true);
    // iter218 — 10VK uses the stripped-down PEC for both NEW and EDIT paths.
    // Flag is kept named `is10vkNew` for continuity; it now means "render the
    // minimal 10VK form variant" regardless of create vs edit.
    const isTenVKEdit = p && Number(p.kit_vial_count) === 10;
    setIs10vkNew(!!isTenVKEdit);
    if (isTenVKEdit) loadTenvkLots(p.id);
    initialFormRef.current = JSON.stringify(p);
    setRestoreDraft(captureDraftOnOpen(p.id, p));
    setDialogOpen(true);
  };

  // iter156.8 — When the admin lands on /admin?tab=products&new=1[&tier=X] or
  // &edit={product_id} (linked from IMS row buttons), auto-open the product
  // form on that product so the workflow flows without a manual find.
  //
  // iter210 — CREATE PEC from IMS: `?tab=products&create_from_lot={lot_id}`
  // fetches BOTH SUPPLY and SELECT IMS rows, finds this lot, prefills the
  // New Product dialog from IMS-owned identity fields, and after Save the
  // POST /admin/products call passes `?lot_id=` so the backend links the
  // new PEC to the lot. Dead PECs are never read.
  useEffect(() => {
    if (tab !== "products") return;
    if (!products || products.length === 0) return;
    try {
      const url = new URL(window.location.href);
      const newFlag = url.searchParams.get("new");
      const editId = url.searchParams.get("edit");
      const tierParam = url.searchParams.get("tier");
      const createFromLot = url.searchParams.get("create_from_lot");
      const createFrom10vkLot = url.searchParams.get("create_from_10vk_lot");
      if (!newFlag && !editId && !createFromLot && !createFrom10vkLot) return;
      if (createFrom10vkLot) {
        (async () => {
          try {
            const { data } = await api.get(`/admin/inventory/10vk/lots`);
            const row = (data?.rows || []).find((r) => r.lot_id === createFrom10vkLot);
            if (!row) {
              toast.error("10VK IMS row not found. It may have been deleted.");
              return;
            }
            setProductTierTab("10vk");
            const nm = String(row.name || "").trim();
            const slugCandidate = nm.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
            const prefill = {
              ...emptyProduct,
              tier: "10vk",
              unit_label: "10-vial Kit",
              name: nm,
              slug: slugCandidate,
              kit_vial_count: 10,
              testing_status: "untested",
              kits_in_stock: 0,
              category: "peptide",
              is_draft: false,
              active: true,
            };
            setProductForm(prefill);
            setPricingModel(inferPricingModel(prefill));
            setEditingId(null);
            setSlugTouched(true);
            setIs10vkNew(true);
            initialFormRef.current = JSON.stringify(prefill);
            setRestoreDraft(null);
            // Load eligible 10VK lots (null = creating; shows orphans) then
            // auto-tick the originating lot so the operator doesn't re-select.
            await loadTenvkLots(null);
            setTenvkLinked(new Set([createFrom10vkLot]));
            setDialogOpen(true);
          } catch (e) {
            toast.error(formatApiErrorDetail(e) || "Failed to load 10VK IMS row for CREATE PEC");
          }
        })();
      } else if (createFromLot) {
        (async () => {
          try {
            // Search both tiers to locate the lot.
            const [sup, sel] = await Promise.all([
              api.get(`/admin/inventory/supply`),
              api.get(`/admin/inventory/select`),
            ]);
            const all = [...(sup.data?.rows || []), ...(sel.data?.rows || [])];
            const row = all.find((r) => r.lot_id === createFromLot);
            if (!row) {
              toast.error("IMS row not found. It may have been deleted.");
              return;
            }
            const rowTier = (row.tier || "supply").toLowerCase();
            setProductTierTab(rowTier === "select" ? "select" : "supply");
            const slugCandidate = String(row.name || "")
              .toLowerCase().trim()
              .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
            const mg = String(row.variant_mg ?? "").trim();
            const prefill = {
              ...emptyProduct,
              tier: rowTier,
              unit_label: `${row.vials_per_kit || 3} vials/kit`,
              name: row.name || "",
              slug: slugCandidate,
              sku: row.sku || "",
              variants: mg
                ? [{ mg, price: "", sku: row.sku || "" }]
                : [],
              variant_unit: row.variant_unit || "mg",
              vials_per_kit: row.vials_per_kit || 10,
              is_draft: false,
              __lot_id: createFromLot,
            };
            setProductForm(prefill);
            setPricingModel(inferPricingModel(prefill));
            setEditingId(null);
            setSlugTouched(true);
            initialFormRef.current = JSON.stringify(prefill);
            setRestoreDraft(null);
            setDialogOpen(true);
          } catch (e) {
            toast.error(formatApiErrorDetail(e) || "Failed to load IMS row for CREATE PEC");
          }
        })();
      } else if (newFlag === "1") {
        if (tierParam === "supply" || tierParam === "select") setProductTierTab(tierParam);
        setTimeout(() => openNew(), 0);
      } else if (editId) {
        const p = products.find((x) => x.id === editId);
        if (p) {
          if (p.tier === "supply" || p.tier === "select") setProductTierTab(p.tier);
          setTimeout(() => openEdit(p), 0);
        } else {
          toast.error("Product not found. It may have been deleted.");
        }
      }
      url.searchParams.delete("new");
      url.searchParams.delete("edit");
      url.searchParams.delete("tier");
      url.searchParams.delete("create_from_lot");
      url.searchParams.delete("create_from_10vk_lot");
      window.history.replaceState({}, "", url.toString());
    } catch (_) { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, products]);

  // Debounced auto-save of the open product form to localStorage. Skips writes
  // when the dialog isn't open or when the form is unchanged from its opening
  // snapshot (so simply opening a dialog doesn't clobber an existing draft
  // before the user has a chance to hit "Restore").
  useEffect(() => {
    if (!dialogOpen) return;
    if (initialFormRef.current === null) return;
    const currentSnap = JSON.stringify(productForm);
    if (currentSnap === initialFormRef.current) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey(editingId),
          JSON.stringify({ data: productForm, savedAt: new Date().toISOString() })
        );
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [productForm, dialogOpen, editingId]);

  // Auto-generate slug from name while creating a NEW product.
  // Once editing an existing product OR the user has manually typed a slug, stop overriding.
  const [slugTouched, setSlugTouched] = useState(false);
  const setName = (name) => {
    setProductForm((f) => {
      const next = { ...f, name };
      if (!editingId && !slugTouched) {
        next.slug = name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-");
      }
      return next;
    });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    // iter203.4 — Tier-required guard. Supply pricing flows entirely from
    // the Assigned Tier. A Supply product with `active=true` (Live) and no
    // supply_tier_id would render on the storefront with the legacy $0/
    // product.price fallback — blocked here so the operator has to pick a
    // tier first. Drafts / paused Supply rows are still allowed
    // to save tier-less so operators can stage them.
    // 10VK exemption — 10-Vial Untested Kits never carry a supply_tier_id;
    // they price directly from `product.price`. Skip the tier-required guard
    // for products where kit_vial_count===10 and testing_status==="untested".
    if (
      productForm.tier === "supply"
      && !(Number(productForm.price) > 0)
      && !(productForm.variants || []).some((v) => String(v.mg ?? "").trim() !== "" && Number(v.price) > 0)
      && productForm.active
      && !(Number(productForm.kit_vial_count) === 10)
    ) {
      toast.error("Enter a 3-vial kit price before publishing.");
      return;
    }
    try {
      const rawMax = productForm.max_qty_per_order;
      const parsedMax = rawMax === "" || rawMax === null || rawMax === undefined ? null : Number(rawMax);
      const tierValue = productForm.tier || null;
      // Normalize mg-per-vial variants: drop rows with blank mg or blank/invalid price.
      const rawVariants = (pricingModel === "multi-mg" || tierValue === "supply")
        ? (Array.isArray(productForm.variants) ? productForm.variants : [])
        : [];
      const normalizedVariants = rawVariants
        .map((v) => ({
          mg: String(v.mg ?? "").trim(),
          price: v.price === "" || v.price === null || v.price === undefined ? null : Number(v.price),
          in_stock: v.in_stock !== false,
          // Iter118 — Select-tier inventory countdown. `null` = untracked
          // (no badge, no decrement). `0` is a real value (out of stock,
          // auto-flips in_stock=false on write).
          vials_in_stock: v.vials_in_stock === null || v.vials_in_stock === undefined || v.vials_in_stock === "" ? null : Math.max(0, parseInt(v.vials_in_stock, 10) || 0),
          ...(tierValue === "supply"
            ? { price_10vial: v.price_10vial === "" || v.price_10vial === null || v.price_10vial === undefined ? null : Number(v.price_10vial) }
            : {}),
        }))
        .filter((v) => v.mg !== "" && v.price !== null && isFinite(v.price) && v.price > 0);

      // iter203.2 — SUPPLY single-strength bridge. Supply-tier products
      // that use the flat-price / tier-price model still need a canonical
      // strength that downstream readers (CPV MG picker, IMS Phase 1A
      // receive `_resolve_line_meta`, storefront mg display) can see —
      // all of those read `product.variants[].mg` exclusively, never
      // `kit_vial_strength_mg`. When the operator types an mg into the
      // "Mg per vial" input on the Supply Price Tier card, mirror it into
      // a single-row `variants[]` so the strength contract lines up with
      // every existing consumer. Leaves `variants=[]` untouched when the
      // Multi-MG model is picked (that mode manages its own variants[])
      // or when mg is blank. Never fires on SELECT products.
      const supplyStrengthTrim = String(productForm.kit_vial_strength_mg || "").trim();
      const finalVariants = (
        tierValue === "supply"
        && pricingModel !== "multi-mg"
        && supplyStrengthTrim !== ""
        && rawVariants.length === 0
      )
        ? [{
            mg: supplyStrengthTrim,
            // Supply sell price is authoritative via `supply_tier_id` (see
            // orders.py L676-688). When a tier is assigned the tier's
            // price wins and `product.price` is ignored at checkout; we
            // still store `product.price` on the variant so tier-less
            // legacy rows keep a sane per-vial reference number.
            price: Number(productForm.price) || 0,
            in_stock: true,
            vials_in_stock: null,
          }]
        : normalizedVariants;
      const payload = {
        ...productForm,
        price: Number(productForm.price),
        // Legacy per-product kit prices are retired — Select ladder handles them.
        price_3vial: null,
        price_5vial: null,
        variants: finalVariants,
        stock: Number(productForm.stock),
        tier: tierValue,
        // Supply-tier products can't be flagged as US-lyophilized by policy.
        is_lyo_usa: tierValue === "supply" ? false : !!productForm.is_lyo_usa,
        max_qty_per_order: parsedMax && parsedMax > 0 ? parsedMax : null,
        // iter184 — Supply fixed-tier pricing fields. Only carry on Supply; nulled on Select.
        supply_tier_id: tierValue === "supply" ? (productForm.supply_tier_id || null) : null,
        price_10vial: tierValue === "supply" && productForm.price_10vial !== null && productForm.price_10vial !== "" && productForm.price_10vial !== undefined
          ? Number(productForm.price_10vial)
          : null,
        // SUPPLY multi-strength: per-strength rows carry their own 3/10-vial prices;
        // product-level fields mirror the lowest row price for listings.
        ...(tierValue === "supply" && finalVariants.length > 0 ? (() => {
          const p3 = finalVariants.map((v) => v.price).filter((n) => n > 0);
          const p10 = finalVariants.map((v) => v.price_10vial).filter((n) => n > 0);
          return { price: Math.min(...p3), price_10vial: p10.length ? Math.min(...p10) : null };
        })() : {}),
        kit_vial_count: tierValue === "supply" ? 3 : null,
        // iter203.2 — Mirror `kit_vial_count → vials_per_kit` on Supply
        // saves. The Supply checkout stock-demand path in
        // routes/orders.py L694 reads `vials_per_kit` (not
        // `kit_vial_count`) and falls back to the hard-coded 10 when the
        // field is absent — see recon anomaly. Mirroring here keeps a
        // single operator input authoritative for both readers without
        // touching backend/order logic.
        vials_per_kit: tierValue === "supply"
          ? 3
          : (productForm.vials_per_kit === "" || productForm.vials_per_kit === null || productForm.vials_per_kit === undefined
              ? 10
              : Math.max(1, parseInt(productForm.vials_per_kit, 10) || 10)),
        kit_vial_strength_mg: tierValue === "supply" ? (String(productForm.kit_vial_strength_mg || "").trim() || null) : null,
        spec: Array.isArray(productForm.spec)
          ? productForm.spec
              .map((s) => ({ key: String(s?.key || "").trim(), value: String(s?.value || "").trim() }))
              .filter((s) => s.key && s.value)
          : [],
        is_blend: !!productForm.is_blend,
        components: (productForm.is_blend && Array.isArray(productForm.components))
          ? productForm.components
              .map((c) => ({ name: String(c?.name || "").trim(), mg: String(c?.mg || "").trim() }))
              .filter((c) => c.name)
          : [],
      };
      // iter210.6 — Blend requires ≥1 valid component (name required).
      if (payload.is_blend && payload.components.length === 0) {
        toast.error("Blend products require at least one component (name is mandatory).");
        return;
      }
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        // iter210 — CREATE PEC from IMS lot passes ?lot_id so the backend
        // links the new PEC to the lot on insert. `__lot_id` is a
        // frontend-only marker set by the create_from_lot handler; strip
        // it before sending.
        const lotId = productForm?.__lot_id || null;
        const cleanPayload = { ...payload };
        delete cleanPayload.__lot_id;
        const qs = lotId ? `?lot_id=${encodeURIComponent(lotId)}` : "";
        await api.post(`/admin/products${qs}`, cleanPayload);
        toast.success(lotId ? "PEC created and linked to IMS lot" : "Product created");
      }
      clearDraft(editingId);
      setRestoreDraft(null);
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // 10VK PEC save — bypasses the SUPPLY/SELECT `saveProduct` logic. Sends the
  // loaded product back with only the PEC fields overridden; inventory
  // fields are stripped (IMS is the sole authority). Then applies IMS
  // link/unlink diffs via PUT /admin/inventory/10vk/lots/{id}/link.
  const save10vkProduct = async (e) => {
    e.preventDefault();
    if (tenvkBusy) return;
    const price = Number(productForm.price);
    if (!productForm.name?.trim()) { toast.error("Product name is required"); return; }
    if (!isFinite(price) || price <= 0) { toast.error("Enter a valid selling price"); return; }
    setTenvkBusy(true);
    try {
      const payload = {
        ...productForm,
        name: productForm.name.trim(),
        slug: (() => {
          const base = productForm.slug || productForm.name.trim().toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
          return !editingId && productForm.testing_status === "tested" && !base.endsWith("-tested") ? `${base}-tested` : base;
        })(),
        sku: String(productForm.sku || "").trim() || null,
        price,
        image_url: productForm.image_url || null,
        kit_vial_strength_mg: String(productForm.kit_vial_strength_mg ?? "").trim() || null,
        active: productForm.active !== false,
        tier: "10vk",
        category: productForm.category || "peptide",
        unit_label: productForm.unit_label || "10-vial Kit",
        kit_vial_count: 10,
        testing_status: productForm.testing_status === "tested" ? "tested" : "untested",
        oos_override: false,
      };
      delete payload.kits_in_stock;
      delete payload.in_stock;
      delete payload.stock;
      delete payload.__lot_id;
      let productId = editingId;
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
      } else {
        const { data } = await api.post("/admin/products", payload);
        productId = data.id;
      }
      const toLink = [...tenvkLinked].filter((id) => !tenvkInitialLinked.has(id));
      const toUnlink = [...tenvkInitialLinked].filter((id) => !tenvkLinked.has(id));
      const linkErrors = [];
      for (const lotId of toLink) {
        try { await api.put(`/admin/inventory/10vk/lots/${lotId}/link`, { product_id: productId }); }
        catch (err) { linkErrors.push(formatApiErrorDetail(err.response?.data?.detail)); }
      }
      for (const lotId of toUnlink) {
        try { await api.put(`/admin/inventory/10vk/lots/${lotId}/link`, { product_id: null }); }
        catch (err) { linkErrors.push(formatApiErrorDetail(err.response?.data?.detail)); }
      }
      if (linkErrors.length) toast.error(`Product saved, but IMS link failed: ${linkErrors.join("; ")}`);
      else toast.success(editingId ? "10VK product updated" : "10VK product created");
      clearDraft(editingId);
      setRestoreDraft(null);
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTenvkBusy(false);
    }
  };

  const delete10vkProduct = async () => {
    if (!editingId || tenvkBusy) return;
    if (!confirm(`DELETE "${productForm.name}" from the 10VK catalog?\n\nIMS lots, quantities and order history are preserved. This cannot be undone.`)) return;
    setTenvkBusy(true);
    try {
      await api.delete(`/admin/products/${editingId}`);
      toast.success("10VK product deleted — IMS lots preserved");
      clearDraft(editingId);
      setRestoreDraft(null);
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTenvkBusy(false);
    }
  };

  const delProduct = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const publishDraft = async (p) => {
    if (!confirm(`Publish "${p.name}" live at $${p.price.toFixed(2)}?\n\nCancel if you want to Edit the price/details first.`)) return;
    try {
      await api.post(`/admin/products/${p.id}/publish`);
      toast.success(`${p.name} published`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Publish failed");
    }
  };

  const duplicateToOtherTier = async (p) => {
    const target = (p.tier === "select") ? "Supply" : "Select";
    if (!confirm(
      `Create a separate ${target} row for "${p.name}"?\n\n` +
      `The new row will inherit price, image, and description but will have its own stock, SKU, and inventory controls. ` +
      `You can edit them independently after creation.`
    )) return;
    try {
      const { data } = await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success(`Created "${data.name}" on the ${target} tier`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Duplicate failed");
    }
  };

  const inputCls = "mt-2 rounded-none border-borderSubtle bg-transparent focus-visible:ring-0 focus-visible:border-oxblood text-[13px]";

  // iter204 — Admin left-nav navigation handler. Preserves deep-link contract.
  const navigateTab = (id) => {
    setTab(id);
    try {
      const u = new URL(window.location.href);
      if (id) u.searchParams.set("tab", id);
      else u.searchParams.delete("tab");
      window.history.replaceState({}, "", u.toString());
    } catch (_) {}
  };
  const isFocusModeTab = tab === "inventory" || tab === "suppliers-directory" || tab === "reports";

  return (
    <div className="flex bg-slate-50 min-h-screen" data-testid="admin-shell">
      <AdminSidebar activeTab={tab} onNavigate={navigateTab} />
      <div className="flex-1 min-w-0">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 lg:py-14" data-testid="admin-page">
      {/* Operator Console — Admin Dashboard only. Renders exclusively on the
          /admin landing (no tab selected). Every other Admin tab suppresses
          the entire section. */}
      {!tab && (
        <>
          <p className="overline mb-3" data-testid="operator-console-heading">Operator console</p>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-10">Admin.</h1>
        </>
      )}

      {/* Phase Purdy hardening (iter190) — restrained partial-load banner.
          Only renders when one or more Admin datasets failed to load, so
          Admins can distinguish "everything is really empty" from "network
          or session issue". Non-blocking; other tabs keep their data. */}
      {loadWarning && (
        <div
          className="mb-6 flex items-center justify-between gap-4 border border-clayBorder bg-clayBg text-clay px-4 py-2.5 rounded-sm text-[13px] font-mono"
          data-testid="admin-load-warning"
          role="status"
        >
          <span className="uppercase tracking-[0.16em]">{loadWarning}</span>
          <button
            type="button"
            onClick={load}
            className="uppercase tracking-[0.18em] text-[12px] px-3 py-1 border border-clay hover:bg-clay hover:text-bone transition-colors rounded-sm"
            data-testid="admin-load-warning-retry"
          >
            Retry
          </button>
        </div>
      )}

      {/* Phase 3A — always mount so the event-outbox nudge surfaces regardless
          of tab. Component self-hides when clean. */}
      <EventOutboxNudge />

      {!tab && (
        <div data-testid="operator-console-body">
          <SalesChart />
          <SalesSnapshot dashboard={dashboard} />
          <div className="-mt-6 mb-6 flex justify-end gap-2">
            <a
              href="/shop?tier=supply"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
              data-testid="view-public-supply-btn"
              title="Open the public Supply catalog in a new tab"
            >
              View Supply ↗
            </a>
            <a
              href="/shop?tier=select"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DDA94E] text-[#DDA94E] hover:bg-[#DDA94E] hover:text-black tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
              data-testid="view-public-select-btn"
              title="Open the public Select (R-1617) catalog in a new tab"
            >
              View Select ↗
            </a>
            <a
              href="/shop/10-vial"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-charcoal text-charcoal hover:bg-charcoal hover:text-bone tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
              data-testid="view-public-10vk-btn"
              title="Open the public 10-Vial Kits catalog in a new tab"
            >
              View 10VK ↗
            </a>
            <button
              onClick={sendWeeklyDigest}
              disabled={digestBusy}
              className="inline-flex items-center gap-2 px-4 py-2 text-[13px] uppercase tracking-[0.22em] text-charcoal hover:text-oxblood border border-borderSubtle hover:border-oxblood rounded-full transition-colors disabled:opacity-50"
              data-testid="send-weekly-digest-btn"
            >
              <Mail size={12} strokeWidth={2}/> {digestBusy ? "Sending…" : "Send weekly digest now"}
            </button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); try { const u = new URL(window.location.href); u.searchParams.set("tab", v); window.history.replaceState({}, "", u.toString()); } catch (_) {} }} className="w-full">
        {/* iter184 Phase 1 — Orders workspace runs in FOCUS mode: full module
            nav is hidden and replaced with a contextual "← Admin  Orders" header.
            Other modules still render the full nav (per scope lock — Phase 1
            applies this behavior to Orders only). */}
        {tab === "orders" || tab === "inventory" || tab === "reports" || tab === "suppliers-directory" ? (
          <div className="hidden" data-testid={`${tab || "focus"}-focus-header`} />
        ) : (
        <TabsList className="!hidden bg-transparent p-0 h-auto border-b border-borderSubtle rounded-none w-full justify-start flex flex-wrap gap-x-8 gap-y-1">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Orders · {orders.length}
          </TabsTrigger>
          <TabsTrigger value="fulfillment" data-testid="tab-fulfillment" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Ready to Ship
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Products · {products.length}
          </TabsTrigger>
          <TabsTrigger value="trials" data-testid="tab-trials" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Trials · {trials.length}
          </TabsTrigger>
          <TabsTrigger value="codes" data-testid="tab-codes" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Codes · {codes.length}
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Compliance · {complianceRecords.length}
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Customers · {customers.length}
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Messages{contactUnread > 0 ? ` · ${contactUnread}` : ""}
          </TabsTrigger>
          <TabsTrigger value="banner" data-testid="tab-banner" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Banner · {banners.length}
          </TabsTrigger>
          <TabsTrigger value="snippets" data-testid="tab-snippets" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Snippets · {snippets.length}
          </TabsTrigger>
          <TabsTrigger value="mailing" data-testid="tab-mailing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Mailing · {subs.active_count}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Settings
          </TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Access Requests
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            IMS
          </TabsTrigger>
          <TabsTrigger value="purchase-orders" data-testid="tab-purchase-orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            CPV
          </TabsTrigger>
          <TabsTrigger value="redirects" data-testid="tab-redirects" className="rounded-none border-b-2 border-transparent data-[state=active]:border-oxblood data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3 overline">
            Redirects
          </TabsTrigger>
        </TabsList>
        )}

        <TabsContent value="orders" className="mt-8">
          <AdminOrdersCommandCenter
            orders={orders}
            products={products}
            onOpenManualInvoice={() => setManualInvoiceOpen(true)}
            onView={(o) => setViewingOrder(o)}
            onEdit={(o) => setEditingOrder(o)}
            onOpenNote={(o) => openOrderNote(o)}
            onOpenTracking={(o) => openTracking(o)}
            onOpenRefund={(o) => openRefund(o)}
            onDelete={(id, num) => deleteOrder(id, num)}
            onUpdateStatus={(id, v) => updateStatus(id, v)}
          />
        </TabsContent>

        <TabsContent value="products" className="mt-8">
          {/* iter72: sub-tabs to split Supply vs Select product management. */}
          <div className="flex items-center justify-between border-b border-borderSubtle mb-6" data-testid="product-tier-tabs">
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={() => setProductTierTab("supply")}
                className={`text-base uppercase tracking-[0.22em] font-bold py-4 -mb-px border-b-2 transition-colors ${
                  productTierTab === "supply"
                    ? "border-oxblood text-oxblood"
                    : "border-transparent text-charcoal/75 hover:text-charcoal"
                }`}
                data-testid="product-tier-tab-supply"
              >
                Supply · {supplyCount}
              </button>
              <button
                type="button"
                onClick={() => setProductTierTab("select")}
                className={`text-base uppercase tracking-[0.22em] font-bold py-4 -mb-px border-b-2 transition-colors ${
                  productTierTab === "select"
                    ? "border-[#DDA94E] text-[#DDA94E]"
                    : "border-transparent text-charcoal/75 hover:text-charcoal"
                }`}
                data-testid="product-tier-tab-select"
              >
                Select (R-1617) · {selectCount}
              </button>
              <button
                type="button"
                onClick={() => setProductTierTab("10vk")}
                className={`text-base uppercase tracking-[0.22em] font-bold py-4 -mb-px border-b-2 transition-colors ${
                  productTierTab === "10vk"
                    ? "border-charcoal text-charcoal"
                    : "border-transparent text-charcoal/75 hover:text-charcoal"
                }`}
                data-testid="product-tier-tab-10vk"
              >
                10VK · {tenVKCount}
              </button>
            </div>
            <div className="flex items-center gap-2 pb-2" data-testid="admin-view-public-links">
              <a
                href="/shop?tier=supply"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
                data-testid="view-public-supply-btn"
                title="Open the public Supply catalog in a new tab"
              >
                View Supply ↗
              </a>
              <a
                href="/shop?tier=select"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DDA94E] text-[#DDA94E] hover:bg-[#DDA94E] hover:text-black tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
                data-testid="view-public-select-btn"
                title="Open the public Select (R-1617) catalog in a new tab"
              >
                View Select ↗
              </a>
              <a
                href="/shop/10-vial"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-charcoal text-charcoal hover:bg-charcoal hover:text-bone tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
                data-testid="products-view-public-10vk-btn"
                title="Open the public 10-Vial Kits catalog in a new tab"
              >
                View 10VK ↗
              </a>
            </div>
          </div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-[13px] text-charcoal/75">
              Products are shown <span className="text-oxblood font-medium">alphabetically</span> to buyers. The <span className="text-oxblood font-medium">Featured</span> toggle pins one kit as the big hero above the grid.
            </p>
            <div className="flex items-center gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/price-list.pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-3 border border-borderSubtle hover:border-oxblood text-charcoal hover:text-oxblood tracking-[0.2em] text-[13px] uppercase transition-colors rounded-none"
                data-testid="price-list-btn"
                title="Download a printable price list (PDF, US Letter)"
              >
                <Download size={12} strokeWidth={2}/> Price list
              </a>
              <button
                onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-3 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-[13px] uppercase transition-colors rounded-none"
                data-testid="bulk-import-btn"
              >
                <Upload size={12} strokeWidth={2}/> Import CSV
              </button>
              <button
                onClick={() => setVariantsImportOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-3 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-[13px] uppercase transition-colors rounded-none"
                data-testid="variants-import-btn"
                title="Bulk-import mg-per-vial variants across products by SKU"
              >
                <Upload size={12} strokeWidth={2}/> Import mg-variants
              </button>
              <button
                onClick={() => setMergeOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-3 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-[13px] uppercase transition-colors rounded-none"
                data-testid="merge-duplicates-btn"
                title="Find and merge duplicate products into one with mg-variants"
              >
                <Merge size={12} strokeWidth={2}/> Merge duplicates
              </button>
              <button
                onClick={async () => {
                  const tier = window.prompt("Purge COAs on which tier?\n\n  supply  — nuke all Supply-tier COAs\n  select  — nuke all Select-tier COAs\n  all     — nuke every COA in the catalog\n\nType supply, select, or all:");
                  if (!tier) return;
                  const t = tier.trim().toLowerCase();
                  if (!["supply", "select", "all"].includes(t)) {
                    toast.error("Cancelled — invalid tier.");
                    return;
                  }
                  if (!window.confirm(`Purge ALL COAs on ${t.toUpperCase()} tier? This clears every product's has_coa flag, empties every coas[] array, and strips all legacy COA metadata. Storage blobs stay put but nothing will render to buyers. Continue?`)) return;
                  try {
                    const { data } = await api.post("/admin/coas/purge", { tier: t, confirm: "PURGE" });
                    toast.success(`Purged COAs on ${data.scope} tier — ${data.modified} product(s) cleaned.`);
                    load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e) || "Purge failed");
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-3 border border-oxblood/60 hover:border-oxblood text-oxblood tracking-[0.2em] text-[13px] uppercase transition-colors rounded-none"
                data-testid="purge-coas-btn"
                title="Nuke every COA reference on a tier (use when ghost COAs linger after individual deletes)"
              >
                <FileWarning size={12} strokeWidth={2}/> Purge COAs
              </button>
              <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-3 bg-oxblood text-bone tracking-[0.2em] text-[13px] uppercase hover:bg-charcoal transition-colors" data-testid="new-product-btn">
                <Plus size={14}/> {productTierTab === "10vk" ? "Add product" : "New product"}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-products-grid">
            {sortedProducts.map((p) => {
              // iter216 — 10VK minimal card. Shows ONLY: image, name, price,
              // kits in stock + Edit action. SUPPLY/SELECT cards fall
              // through to the existing renderer unchanged.
              if (productTierTab === "10vk") {
                return (
                  <div
                    key={p.id}
                    className="border bg-white p-6 relative border-borderSubtle"
                    data-testid={`admin-product-${p.slug}`}
                  >
                    {p.active === false && (
                      <span className="absolute top-3 right-3 z-10 bg-charcoal text-bone text-[11px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-10vk-hidden-${p.slug}`}>
                        Hidden
                      </span>
                    )}
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-40 object-contain bg-white rounded"
                        data-testid={`admin-10vk-image-${p.slug}`}
                      />
                    ) : (
                      <div
                        className="w-full h-40 flex items-center justify-center border border-borderSubtle rounded text-[13px] text-charcoal/60 font-sans uppercase tracking-widest"
                        data-testid={`admin-10vk-image-missing-${p.slug}`}
                      >
                        No image
                      </div>
                    )}
                    <h3 className="font-sans text-2xl mt-4" data-testid={`admin-10vk-name-${p.slug}`}>{p.name}</h3>
                    <p className="font-sans text-lg mt-1" data-testid={`admin-10vk-price-${p.slug}`}>
                      ${Number(p.price || 0).toFixed(2)}
                    </p>
                    <p className="font-sans text-[13px] text-charcoal/75 mt-1" data-testid={`admin-10vk-kits-${p.slug}`}>
                      Kits in stock: {p.kits_in_stock ?? 0}
                    </p>
                    <div className="flex gap-2 mt-5">
                      <button
                        onClick={() => openEdit(p)}
                        className="flex-1 py-2 border border-borderSubtle hover:border-charcoal hover:text-charcoal text-[13px] overline transition-colors rounded-full"
                        data-testid={`edit-product-${p.slug}`}
                      >
                        <Pencil size={12} className="inline mr-2 -mt-0.5"/> Edit
                      </button>
                    </div>
                  </div>
                );
              }
              return (
              <div
                key={p.id}
                className="border bg-white p-6 relative border-borderSubtle"
                data-testid={`admin-product-${p.slug}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <span className="inline-block bg-charcoal text-bone text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full">{p.sku || "—"}</span>
                        {p.tier === "select" && (
                          <span className="inline-block bg-black text-[#DDA94E] border border-[#DDA94E] text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-tier-select-${p.slug}`} title="Research Select tier (R-1617)">
                            R-1617
                          </span>
                        )}
                        {p.tier === "supply" && (
                          <span className="inline-block bg-bone text-charcoal border border-charcoal text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-tier-supply-${p.slug}`} title="Research Supply tier">
                            Supply
                          </span>
                        )}
                        {p.tier === "supply" && !p.has_coa && (
                          <span
                            className="inline-flex items-center gap-1 bg-[#FFF6E5] text-[#8A5A00] border border-[#D6A650] text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full"
                            data-testid={`admin-no-coa-warning-${p.slug}`}
                            title="No Certificate of Analysis uploaded for this product yet. Upload via the edit dialog."
                          >
                            📄 No COA
                          </span>
                        )}
                        {p.featured && (
                          <span className="inline-block bg-bone text-oxblood border border-oxblood text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-featured-${p.slug}`}>
                            ★ Featured
                          </span>
                        )}
                        {p.active === false && (
                          <span className="inline-block bg-oxblood text-bone text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-paused-${p.slug}`} title="Paused — hidden from buyers. Unpause in the edit form.">
                            ‖ Paused
                          </span>
                        )}
                        {p.is_hot_seller && (
                          <span className="inline-block bg-oxblood text-bone text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-hot-seller-${p.slug}`} title="Marked as HOT SELLER!">
                            🔥 Hot seller
                          </span>
                        )}
                        {p.is_lyo_usa && (
                          <span className="inline-flex items-center gap-1 bg-white text-charcoal border border-borderSubtle text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-lyo-usa-${p.slug}`} title="US Lyophilization">
                            <span className="inline-block w-2.5 h-2.5 rounded-full overflow-hidden" style={{background: "linear-gradient(180deg,#B22234 0 50%,#F5EFE5 50% 100%)"}} aria-hidden />
                            Lyo USA
                          </span>
                        )}
                        {p.max_qty_per_order ? (
                          <span className="inline-block bg-bone text-charcoal border border-charcoal/40 text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-max-qty-${p.slug}`} title={`Max ${p.max_qty_per_order} per order`}>
                            Max {p.max_qty_per_order}/order
                          </span>
                        ) : null}
                        {p.in_stock === false && (
                          <span className="inline-block bg-oxblood text-bone text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-oos-${p.slug}`}>
                            Out of stock
                          </span>
                        )}
                        {p.is_draft && (
                          <span className="inline-block bg-amber-500 text-white text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full" data-testid={`admin-draft-${p.slug}`}>
                            Draft
                          </span>
                        )}
                        {waitlistCounts[p.id] > 0 && (
                          <button
                            onClick={() => openWaitlist(p)}
                            className="inline-flex items-center gap-1.5 bg-oxblood text-bone text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full hover:bg-charcoal transition-colors"
                            title="View buyers on the restock waitlist"
                            data-testid={`admin-waitlist-badge-${p.slug}`}
                          >
                            <Users size={10} strokeWidth={2.5} />
                            {waitlistCounts[p.id]} {waitlistCounts[p.id] === 1 ? "person" : "people"} waiting
                          </button>
                        )}
                      </div>
                      <h3 className="font-serif text-2xl">{p.name}</h3>
                      <p className="text-[13px] text-charcoal/75 font-mono">{p.category} · {p.tier === "supply" ? `${p.kit_vial_count || 3} vials/kit` : p.unit_label}</p>
                      <p className="text-[13px] text-charcoal/75 font-mono">stock {p.vials_in_stock ?? 0}</p>
                    </div>
                  </div>
                  <p className="font-mono text-lg" data-testid={`admin-pec-price-${p.slug}`}>
                    ${(() => {
                      // iter212 — SELECT rows: read the variant-derived price
                      // so the PEC card reflects post-Push state. `product.price`
                      // isn't touched by the Push-to-Product endpoint
                      // (`routes/product_prices.py` sets `variants.$[t].price`
                      // only, by design). SUPPLY and legacy-SELECT-without-
                      // variants keep the existing `p.price` fallback intact.
                      if (p.tier === "select") {
                        const prices = (Array.isArray(p.variants) ? p.variants : [])
                          .map((v) => Number(v?.price))
                          .filter((n) => Number.isFinite(n) && n > 0);
                        if (prices.length) return Math.min(...prices).toFixed(2);
                      }
                      return Number(p.price || 0).toFixed(2);
                    })()}
                  </p>
                </div>
                <p className="text-[13px] text-charcoal/75 mt-3 line-clamp-2">{p.description}</p>
                <CoaDropzone product={p} onChange={() => load()} />
                {/* Image attached indicator — mirrors the CoaDropzone attached
                    pill so admins can see at a glance which products already
                    have a product image on file. Non-interactive. */}
                {p.image_url ? (
                  <div
                    className="mt-3 flex items-center gap-2 border border-emerald-300 bg-emerald-50 rounded-lg px-3 py-2 text-[13px]"
                    data-testid={`image-attached-${p.slug}`}
                  >
                    <FileCheck2 size={14} className="text-emerald-700 shrink-0" />
                    <span className="flex-1 truncate text-emerald-900" title={p.image_url}>
                      Product image attached
                    </span>
                  </div>
                ) : (
                  <div
                    className="mt-3 flex items-center gap-2 border border-borderSubtle bg-white rounded-lg px-3 py-2 text-[13px] text-charcoal/75"
                    data-testid={`image-missing-${p.slug}`}
                  >
                    <span className="w-3 h-3 rounded-full border border-stone/40 shrink-0" />
                    <span className="flex-1 truncate">No product image</span>
                  </div>
                )}
                <div className="flex gap-2 mt-5">
                  <button onClick={() => openEdit(p)} className="flex-1 py-2 border border-borderSubtle hover:border-charcoal hover:text-charcoal text-[13px] overline transition-colors rounded-full" data-testid={`edit-product-${p.slug}`}>
                    <Pencil size={12} className="inline mr-2 -mt-0.5"/> Edit
                  </button>
                  {!p.is_draft && (
                    <button
                      onClick={() => toggleTempVisible(p)}
                      disabled={tempVisibleBusy.has(p.id)}
                      className={`px-3 py-2 border text-[12px] font-mono uppercase tracking-widest transition-colors rounded-full ${
                        p.temp_visible
                          ? "border-oxblood bg-oxblood text-bone hover:bg-charcoal hover:border-charcoal"
                          : "border-borderSubtle text-charcoal/75 hover:border-oxblood hover:text-oxblood"
                      } disabled:opacity-50`}
                      data-testid={`product-temp-visible-toggle-${p.slug}`}
                      aria-pressed={!!p.temp_visible}
                      title={p.temp_visible ? "Visible to the shared temp login" : "Hidden from the temp login"}
                    >
                      {p.temp_visible ? "Temp: ON" : "Temp: OFF"}
                    </button>
                  )}
                  {!p.is_draft && (
                    <button
                      onClick={() => duplicateToOtherTier(p)}
                      className="px-3 py-2 border border-borderSubtle hover:border-charcoal hover:text-charcoal text-[12px] font-mono uppercase tracking-widest transition-colors rounded-full"
                      data-testid={`duplicate-tier-${p.slug}`}
                      title={`Create an independent ${(p.tier === 'select') ? 'Supply' : 'Select'} row for this product`}
                    >
                      → {(p.tier === 'select') ? 'Supply' : 'Select'}
                    </button>
                  )}
                  {p.is_draft && (
                    <button
                      onClick={() => publishDraft(p)}
                      className="px-4 py-2 bg-oxblood text-bone hover:bg-charcoal text-[13px] overline transition-colors rounded-full"
                      data-testid={`publish-draft-${p.slug}`}
                    >
                      Publish
                    </button>
                  )}
                  <button onClick={() => delProduct(p.id)} className="px-3 py-2 border border-borderSubtle hover:border-oxblood hover:text-oxblood transition-colors rounded-full" data-testid={`delete-product-${p.slug}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </TabsContent>

        {/* TRIALS TAB */}
        <TabsContent value="trials" className="mt-8">
          <div className="grid lg:grid-cols-[380px_1fr] gap-8">
            <div className="bg-white border border-borderSubtle rounded-2xl p-6">
              <p className="overline mb-4">Create trial account</p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const { data } = await api.post("/admin/trials", trialForm);
                  setNewTrial(data);
                  setTrialForm(emptyTrial);
                  toast.success(data.email_dispatched
                    ? `Trial created — login details emailed to ${data.email}`
                    : "Trial created — copy the password below");
                  load();
                } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
              }} className="space-y-4" data-testid="trial-form">
                <div>
                  <Label className="overline font-bold text-charcoal">Full name</Label>
                  <Input required value={trialForm.name} onChange={(e) => setTrialForm({...trialForm, name: e.target.value})} className={inputCls} data-testid="trial-name-input"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Email</Label>
                  <Input required type="email" value={trialForm.email} onChange={(e) => setTrialForm({...trialForm, email: e.target.value})} className={inputCls} data-testid="trial-email-input"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Trial length (days)</Label>
                  <Input required type="number" min={1} max={30} value={trialForm.days} onChange={(e) => setTrialForm({...trialForm, days: Number(e.target.value)})} className={inputCls} data-testid="trial-days-input"/>
                </div>
                <button type="submit" className="w-full py-3 bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase hover:bg-oxblood transition-colors rounded-full" data-testid="create-trial-btn">
                  Create trial
                </button>
              </form>

              {newTrial && (
                <div className="mt-6 p-4 bg-[#EDF7EE] border border-[#B7DCB9] rounded-xl" data-testid="new-trial-info">
                  <p className="text-[13px] uppercase tracking-[0.22em] text-[#265427] mb-2">Trial ready</p>
                  {newTrial.email_dispatched ? (
                    <p className="text-[13px] text-[#265427] mb-3 inline-flex items-center gap-1.5" data-testid="trial-email-sent">
                      <Mail size={12} strokeWidth={2}/> Login details emailed to <span className="font-mono">{newTrial.email}</span>
                    </p>
                  ) : (
                    <p className="text-[13px] text-oxblood mb-3" data-testid="trial-email-skipped">
                      Email not sent (RESEND_API_KEY missing) — copy credentials manually.
                    </p>
                  )}
                  <p className="text-[13px] text-charcoal/75 mb-1">Email</p>
                  <p className="font-mono text-sm mb-2" data-testid="new-trial-email">{newTrial.email}</p>
                  <p className="text-[13px] text-charcoal/75 mb-1">Temporary password (shown once)</p>
                  <p className="font-mono text-sm mb-2 bg-white px-2 py-1 rounded" data-testid="new-trial-password">{newTrial.temp_password}</p>
                  <p className="text-[13px] text-charcoal/75">Expires: {new Date(newTrial.expires_at).toLocaleString()}</p>
                  <button onClick={() => { navigator.clipboard.writeText(`Email: ${newTrial.email}\nPassword: ${newTrial.temp_password}\nExpires: ${new Date(newTrial.expires_at).toLocaleString()}`); toast.success("Copied to clipboard"); }} className="mt-3 text-[13px] uppercase tracking-[0.22em] text-oxblood hover:text-charcoal">
                    Copy credentials
                  </button>
                </div>
              )}
            </div>

            <div className="border border-borderSubtle bg-white rounded-2xl overflow-x-auto" data-testid="admin-trials-table">
              <table className="w-full text-sm">
                <thead className="bg-bone border-b border-borderSubtle">
                  <tr className="text-left">
                    {["Name", "Email", "Created", "Expires", "Status", ""].map((h) => (<th key={h} className="overline px-4 py-3">{h}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {trials.map((t) => (
                    <tr key={t.id} className="border-b border-borderSubtle last:border-0" data-testid={`trial-row-${t.id}`}>
                      <td className="px-4 py-3">{t.name}</td>
                      <td className="px-4 py-3 font-mono text-[13px]">{t.email}</td>
                      <td className="px-4 py-3 text-[13px] text-charcoal/75">{new Date(t.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-[13px] text-charcoal/75">{new Date(t.expires_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <span className={`overline px-2 py-1 border ${t.expired ? "bg-borderSubtle text-charcoal/75 border-borderSubtle" : "bg-white text-oxblood border-oxblood"}`}>
                          {t.expired ? "Expired" : "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={async () => { if (confirm(`Revoke trial for ${t.email}?`)) { await api.delete(`/admin/trials/${t.id}`); toast.success("Trial revoked"); load(); }}} className="text-charcoal/75 hover:text-oxblood" data-testid={`revoke-trial-${t.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {trials.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-charcoal/75">No trial accounts yet.</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* DISCOUNT CODES TAB */}
        <TabsContent value="codes" className="mt-8">
          <div className="grid lg:grid-cols-[380px_1fr] gap-8">
            <div className="bg-white border border-borderSubtle rounded-2xl p-6">
              <p className="overline mb-4">Create discount code</p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const payload = {
                    code: codeForm.code,
                    discount_type: codeForm.discount_type,
                    discount_value: Number(codeForm.discount_value),
                    usage_limit: codeForm.usage_limit ? Number(codeForm.usage_limit) : null,
                    expires_at: codeForm.expires_at ? new Date(codeForm.expires_at + "T23:59:59").toISOString() : null,
                    active: codeForm.active,
                    // "" = both shelves; "supply" | "select" = scoped
                    tier: codeForm.tier || null,
                  };
                  await api.post("/admin/discount-codes", payload);
                  setCodeForm(emptyCode);
                  toast.success("Code created");
                  load();
                } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
              }} className="space-y-4" data-testid="code-form">
                <div>
                  <Label className="overline font-bold text-charcoal">Code</Label>
                  <Input required value={codeForm.code} onChange={(e) => setCodeForm({...codeForm, code: e.target.value.toUpperCase()})} className={inputCls} placeholder="WELCOME10" data-testid="code-value-input"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Type</Label>
                  <select value={codeForm.discount_type} onChange={(e) => setCodeForm({...codeForm, discount_type: e.target.value})} className="mt-2 h-11 w-full rounded-none border-0 border-b border-borderSubtle bg-transparent px-0 focus:outline-none focus:border-oxblood" data-testid="code-type-select">
                    <option value="percent">Percent off (%)</option>
                    <option value="fixed">Fixed dollars off ($)</option>
                  </select>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Value {codeForm.discount_type === "percent" ? "(%)" : "($)"}</Label>
                  <Input required type="number" min={1} step="0.01" value={codeForm.discount_value} onChange={(e) => setCodeForm({...codeForm, discount_value: e.target.value})} className={inputCls} data-testid="code-amount-input"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Applies to</Label>
                  <select
                    value={codeForm.tier}
                    onChange={(e) => setCodeForm({...codeForm, tier: e.target.value})}
                    className="mt-2 h-11 w-full rounded-none border-0 border-b border-borderSubtle bg-transparent px-0 focus:outline-none focus:border-oxblood"
                    data-testid="code-tier-select"
                  >
                    <option value="">Both tiers</option>
                    <option value="supply">Research Supply only</option>
                    <option value="select">Research 1617 (Select) only</option>
                  </select>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Usage limit (optional)</Label>
                  <Input type="number" min={1} value={codeForm.usage_limit} onChange={(e) => setCodeForm({...codeForm, usage_limit: e.target.value})} className={inputCls} placeholder="Unlimited"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Expiry (optional)</Label>
                  <Input type="date" value={codeForm.expires_at} onChange={(e) => setCodeForm({...codeForm, expires_at: e.target.value})} className={inputCls}/>
                </div>
                <button type="submit" className="w-full py-3 bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase hover:bg-oxblood transition-colors rounded-full" data-testid="create-code-btn">
                  Create code
                </button>
              </form>
            </div>

            <div className="border border-borderSubtle bg-white rounded-2xl overflow-x-auto" data-testid="admin-codes-table">
              <table className="w-full text-sm">
                <thead className="bg-bone border-b border-borderSubtle">
                  <tr className="text-left">
                    {["Code", "Discount", "Applies to", "Uses", "Expires", "Status", ""].map((h) => (<th key={h} className="overline px-4 py-3">{h}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr key={c.id} className="border-b border-borderSubtle last:border-0" data-testid={`code-row-${c.code}`}>
                      <td className="px-4 py-3 font-mono">{c.code}</td>
                      <td className="px-4 py-3">{c.discount_type === "percent" ? `${c.discount_value}%` : `$${c.discount_value.toFixed(2)}`}</td>
                      <td className="px-4 py-3 text-[13px]" data-testid={`code-tier-${c.code}`}>
                        {c.tier === "select" ? (
                          <span className="overline px-2 py-1 border bg-[#141210] !text-[#DDA94E] border-[#DDA94E]/50">R-1617</span>
                        ) : c.tier === "supply" ? (
                          <span className="overline px-2 py-1 border bg-white border-borderSubtle">Supply</span>
                        ) : (
                          <span className="overline px-2 py-1 border bg-bone border-borderSubtle">Both</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[13px] text-charcoal/75">{c.times_used}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                      <td className="px-4 py-3 text-[13px] text-charcoal/75">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`overline px-2 py-1 border ${c.active ? "bg-white text-oxblood border-oxblood" : "bg-borderSubtle text-charcoal/75 border-borderSubtle"}`}>
                          {c.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={async () => { if (confirm(`Delete code ${c.code}?`)) { await api.delete(`/admin/discount-codes/${c.id}`); toast.success("Deleted"); load(); }}} className="text-charcoal/75 hover:text-oxblood" data-testid={`delete-code-${c.code}`}>
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {codes.length === 0 && (<tr><td colSpan={7} className="px-4 py-8 text-center text-charcoal/75">No discount codes yet.</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* COMPLIANCE AUDIT TAB */}
        <TabsContent value="compliance" className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <p className="text-sm text-charcoal/75 max-w-2xl">
              Every non-admin buyer who has signed the Research-Use Acknowledgment. Use this for your legal audit trail.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/compliance-export.csv`}
              className="inline-flex items-center gap-2 px-5 py-3 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase transition-colors rounded-full"
              data-testid="compliance-download-csv"
            >
              <Download size={14} strokeWidth={2}/> Download CSV
            </a>
          </div>
          <div className="border border-borderSubtle bg-white rounded-2xl overflow-x-auto" data-testid="admin-compliance-table">
            <table className="w-full text-sm">
              <thead className="bg-bone border-b border-borderSubtle">
                <tr className="text-left">
                  {["Signed name", "Email", "Researcher type", "Acknowledged", "IP", "Trial", ""].map((h) => (
                    <th key={h} className="overline px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {complianceRecords.map((r) => (
                  <tr key={r.id} className="border-b border-borderSubtle last:border-0" data-testid={`compliance-row-${r.id}`}>
                    <td className="px-4 py-3">{r.signed_name || <span className="text-charcoal/75">—</span>}</td>
                    <td className="px-4 py-3 font-mono text-[13px]">{r.email}</td>
                    <td className="px-4 py-3 overline">{r.researcher_type || "—"}</td>
                    <td className="px-4 py-3 text-[13px] text-charcoal/75">
                      {r.acknowledged_at ? new Date(r.acknowledged_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[13px] text-charcoal/75">{r.ip || "—"}</td>
                    <td className="px-4 py-3">
                      {r.is_trial ? <span className="overline text-oxblood">Trial</span> : <span className="text-charcoal/75">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deleteComplianceRecord(r)}
                        className="text-charcoal/75 hover:text-oxblood p-1.5 transition-colors"
                        title="Clear compliance record"
                        aria-label={`Delete compliance record for ${r.email}`}
                        data-testid={`compliance-delete-${r.id}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
                {complianceRecords.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-charcoal/75">No signed acknowledgments yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* CUSTOMERS TAB */}
        <TabsContent value="customers" className="mt-8">
          <div className="mb-4 relative max-w-md">
            <Search size={14} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/75" />
            <input
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Search customers by email or name"
              className="w-full h-11 pl-10 pr-4 rounded-full bg-white border border-borderSubtle text-sm placeholder:text-charcoal/75 focus:outline-none focus:border-charcoal transition-colors"
              data-testid="admin-customer-search"
            />
          </div>
          {allTags.length > 0 && (
            <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="customer-tag-filters">
              <span className="overline text-charcoal/75 mr-1">Filter by tag</span>
              <button
                onClick={() => setTagFilter("")}
                className={`text-[13px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border transition-colors ${
                  !tagFilter ? "bg-charcoal text-bone border-charcoal" : "border-borderSubtle text-charcoal hover:border-charcoal"
                }`}
                data-testid="tag-filter-all"
              >
                All
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTagFilter(t === tagFilter ? "" : t)}
                  className={`text-[13px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border transition-colors ${
                    tagFilter === t ? "bg-oxblood text-bone border-oxblood" : "border-borderSubtle text-charcoal hover:border-oxblood hover:text-oxblood"
                  }`}
                  data-testid={`tag-filter-${t.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
          <div className="border border-borderSubtle bg-white rounded-2xl overflow-x-auto" data-testid="admin-customers-table">
            <table className="w-full text-[15px]">
              <thead className="bg-bone border-b border-borderSubtle">
                <tr className="text-left">
                  {["Customer", "Tags", "Orders", "Lifetime spend", "Last order", "Notes", "Actions"].map((h) => (
                    <th key={h} className="overline px-4 py-3 text-[13px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((c) => (
                  <tr key={c.id} className="border-b border-borderSubtle last:border-0 align-top" data-testid={`customer-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium flex items-center gap-2">
                        {c.name || <span className="text-charcoal/75">—</span>}
                        {c.is_trial && <span className="overline text-oxblood">Trial</span>}
                      </p>
                      <p className="text-[13px] text-charcoal/75 font-mono">{c.email}</p>
                      <p className="text-[12px] text-charcoal/75 font-mono mt-1">
                        joined {c.created_at ? new Date(c.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3 min-w-[180px]" data-testid={`customer-tags-${c.id}`}>
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {(c.tags || []).map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 text-[13px] uppercase tracking-widest px-2 py-1 rounded-full bg-oxblood/10 text-oxblood border border-oxblood/30" data-testid={`customer-tag-chip-${c.id}-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                            {t}
                            <button
                              onClick={() => removeTag(c, t)}
                              className="hover:text-charcoal ml-0.5"
                              aria-label={`Remove tag ${t}`}
                              data-testid={`remove-tag-${c.id}-${t.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <X size={9} strokeWidth={2.5}/>
                            </button>
                          </span>
                        ))}
                        {tagInputFor === c.id ? (
                          <input
                            autoFocus
                            value={tagDraft}
                            onChange={(e) => setTagDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); addTagTo(c); }
                              if (e.key === "Escape") { setTagInputFor(null); setTagDraft(""); }
                            }}
                            onBlur={() => { if (tagDraft.trim()) addTagTo(c); else { setTagInputFor(null); setTagDraft(""); } }}
                            placeholder="VIP, Lab, Wholesale…"
                            maxLength={24}
                            className="text-[12px] px-2 py-1 border border-charcoal rounded-full bg-white focus:outline-none w-28"
                            data-testid={`tag-input-${c.id}`}
                          />
                        ) : (
                          (c.tags || []).length < 8 && (
                            <button
                              onClick={() => { setTagInputFor(c.id); setTagDraft(""); }}
                              className="text-[13px] uppercase tracking-widest text-charcoal/75 hover:text-oxblood transition-colors border border-dashed border-borderSubtle hover:border-oxblood rounded-full px-2 py-1"
                              data-testid={`add-tag-btn-${c.id}`}
                            >
                              + Tag
                            </button>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono">{c.orders_count}</td>
                    <td className="px-4 py-3 font-mono text-oxblood">${c.lifetime_spend.toFixed(2)}</td>
                    <td className="px-4 py-3 text-[13px] text-charcoal/75">
                      {c.last_order_at ? new Date(c.last_order_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : <span>—</span>}
                    </td>
                    <td className="px-4 py-3 min-w-[220px]">
                      {editingNoteId === c.id ? (
                        <div className="space-y-2" data-testid={`note-editor-${c.id}`}>
                          <textarea
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            maxLength={2000}
                            rows={3}
                            className="w-full text-[13px] bg-white border border-borderSubtle rounded p-2 focus:outline-none focus:border-charcoal font-mono"
                            placeholder="Private context — e.g. Prefers ProT-Alpha, net-30…"
                            data-testid={`note-input-${c.id}`}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveNote(c)}
                              className="text-[13px] uppercase tracking-[0.22em] px-3 py-1.5 bg-charcoal text-bone rounded-full hover:bg-oxblood transition-colors"
                              data-testid={`note-save-${c.id}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelNoteEditor}
                              className="text-[13px] uppercase tracking-[0.22em] px-3 py-1.5 text-charcoal/75 hover:text-charcoal"
                              data-testid={`note-cancel-${c.id}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : c.admin_notes ? (
                        <button
                          onClick={() => openNoteEditor(c)}
                          className="text-left text-[13px] text-charcoal hover:text-oxblood transition-colors group"
                          data-testid={`note-display-${c.id}`}
                        >
                          <span className="whitespace-pre-wrap group-hover:underline underline-offset-2">
                            {c.admin_notes}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => openNoteEditor(c)}
                          className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-oxblood transition-colors"
                          data-testid={`note-add-${c.id}`}
                        >
                          + Add note
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <a
                          href={`mailto:${c.email}`}
                          className="text-charcoal/75 hover:text-oxblood transition-colors"
                          aria-label={`Email ${c.email}`}
                          data-testid={`customer-email-btn-${c.id}`}
                        >
                          <Mail size={14}/>
                        </a>
                        <button
                          onClick={() => setConfirmDeleteCustomer(c)}
                          disabled={customerBusyId === c.id}
                          className="text-charcoal/75 hover:text-oxblood transition-colors disabled:opacity-50"
                          title="Delete customer (keeps their order history)"
                          aria-label={`Delete ${c.email}`}
                          data-testid={`delete-customer-btn-${c.id}`}
                        >
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCustomers.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-charcoal/75">
                    {customerSearch.trim() || tagFilter ? "No customers match this filter." : "No customers yet."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* MESSAGES TAB — contact form submissions */}
        <TabsContent value="messages" className="mt-8">
          <AdminContactSubmissions onCountChange={setContactUnread} />
        </TabsContent>

        {/* BANNER TAB */}
        <TabsContent value="banner" className="mt-8">
          <div className="grid md:grid-cols-[420px_1fr] gap-8">
            <div className="border border-borderSubtle bg-white p-6" data-testid="banner-form-panel">
              <p className="overline mb-4">{editingBannerId ? "Edit banner" : "New banner"}</p>
              <form onSubmit={saveBanner} className="space-y-4" data-testid="banner-form">
                <div>
                  <Label className="overline font-bold text-charcoal">Message</Label>
                  <Input
                    required maxLength={200}
                    value={bannerForm.message}
                    onChange={(e) => setBannerForm({ ...bannerForm, message: e.target.value })}
                    placeholder="10% off through Friday — code FRIDAY10"
                    className={inputCls}
                    data-testid="banner-message-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline font-bold text-charcoal">Link label</Label>
                    <Input
                      value={bannerForm.link_label}
                      onChange={(e) => setBannerForm({ ...bannerForm, link_label: e.target.value })}
                      placeholder="Shop now"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <Label className="overline font-bold text-charcoal">Link URL</Label>
                    <Input
                      value={bannerForm.link_url}
                      onChange={(e) => setBannerForm({ ...bannerForm, link_url: e.target.value })}
                      placeholder="/shop"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setBannerForm({ ...bannerForm, enabled: !bannerForm.enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bannerForm.enabled ? "bg-oxblood" : "bg-borderSubtle"}`}
                    data-testid="banner-enabled-toggle"
                    aria-pressed={bannerForm.enabled}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${bannerForm.enabled ? "translate-x-6" : "translate-x-1"}`}/>
                  </button>
                  <p className="text-sm font-medium">{bannerForm.enabled ? "Live on the catalog" : "Draft"}</p>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setBannerForm({ ...bannerForm, marquee: !bannerForm.marquee })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bannerForm.marquee ? "bg-charcoal" : "bg-borderSubtle"}`}
                    data-testid="banner-marquee-toggle"
                    aria-pressed={bannerForm.marquee}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${bannerForm.marquee ? "translate-x-6" : "translate-x-1"}`}/>
                  </button>
                  <div>
                    <p className="text-sm font-medium">{bannerForm.marquee ? "Marquee scroll" : "Static text"}</p>
                    <p className="text-[13px] text-charcoal/75">
                      {bannerForm.marquee ? "Message glides horizontally — pauses on hover." : "Toggle on to make longer messages scroll continuously."}
                    </p>
                  </div>
                </div>
                {bannerForm.marquee && (
                  <div className="pt-1" data-testid="banner-speed-wrapper">
                    <Label className="overline font-bold text-charcoal">Scroll speed</Label>
                    <Select value={bannerForm.speed} onValueChange={(v) => setBannerForm({ ...bannerForm, speed: v })}>
                      <SelectTrigger className="mt-2 rounded-none border-borderSubtle bg-white focus:ring-0" data-testid="banner-speed-select">
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="glacial">Glacial — 90s per pass</SelectItem>
                        <SelectItem value="slow">Slow — 50s per pass</SelectItem>
                        <SelectItem value="medium">Medium — 15s per pass</SelectItem>
                        <SelectItem value="fast">Fast — 8s per pass</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="pt-1" data-testid="banner-tier-wrapper">
                  <Label className="overline font-bold text-charcoal">Show on tier</Label>
                  <Select value={bannerForm.tier || "both"} onValueChange={(v) => setBannerForm({ ...bannerForm, tier: v === "both" ? "" : v })}>
                    <SelectTrigger className="mt-2 rounded-none border-borderSubtle bg-white focus:ring-0" data-testid="banner-tier-select">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both tiers (Supply + Select)</SelectItem>
                      <SelectItem value="supply">Research Supply only</SelectItem>
                      <SelectItem value="select">Research Select only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 mt-1 font-mono">
                    Tier-specific banners take priority over "both tiers" banners.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="submit" className="flex-1 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase transition-colors rounded-full" data-testid="banner-save-btn">
                    {editingBannerId ? "Save changes" : "Publish banner"}
                  </button>
                  {editingBannerId && (
                    <button type="button" onClick={openNewBanner} className="py-2.5 px-4 border border-borderSubtle text-[13px] overline rounded-full">
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="space-y-3" data-testid="banner-list">
              {banners.length === 0 ? (
                <div className="border border-dashed border-borderSubtle bg-white p-8 text-center text-sm text-charcoal/75">
                  No banners yet. Create one on the left to show a promo message above the catalog.
                </div>
              ) : (
                banners.map((b) => (
                  <div key={b.id} className={`border p-4 flex justify-between items-center gap-4 ${b.enabled ? "border-oxblood bg-white" : "border-borderSubtle bg-white"}`} data-testid={`banner-row-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`overline ${b.enabled ? "text-oxblood" : "text-charcoal/75"}`}>{b.enabled ? "Live" : "Draft"}</span>
                        <span className="text-[12px] font-mono uppercase tracking-widest text-charcoal/75">
                          {new Date(b.created_at).toLocaleDateString()}
                        </span>
                        {b.tier ? (
                          <span
                            className={`inline-flex items-center text-[12px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${b.tier === "select" ? "bg-[#0B0908] text-[#DDA94E] border border-[#DDA94E]/40" : "bg-charcoal text-bone"}`}
                            data-testid={`banner-tier-badge-${b.id}`}
                          >
                            {b.tier === "select" ? "Select" : "Supply"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[12px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-bone text-charcoal/75 border border-borderSubtle" data-testid={`banner-tier-badge-${b.id}`}>
                            Both
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-charcoal truncate">{b.message}</p>
                      {b.link_url && <p className="text-[13px] text-charcoal/75 truncate mt-0.5">→ {b.link_label || "link"} · {b.link_url}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => openEditBanner(b)} className="text-charcoal/75 hover:text-charcoal p-2" data-testid={`banner-edit-${b.id}`} aria-label="Edit banner">
                        <Pencil size={14}/>
                      </button>
                      <button onClick={() => deleteBanner(b.id)} className="text-charcoal/75 hover:text-oxblood p-2" data-testid={`banner-delete-${b.id}`} aria-label="Delete banner">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="snippets" className="mt-8" data-testid="snippets-tab">
          <div className="grid md:grid-cols-[minmax(0,1fr)_360px] gap-8">
            <div>
              <h3 className="font-serif text-2xl mb-4">Note templates</h3>
              <p className="text-[13px] text-charcoal/75 mb-6 max-w-lg leading-relaxed">
                Save short notes you want to reuse — reorder thank-yous, shipping heads-ups, seasonal quips. They'll show up as one-click chips in the Order Note dialog on the Orders tab. Keep them warm and specific.
              </p>
              <div className="space-y-3" data-testid="snippet-list">
                {snippets.length === 0 && (
                  <p className="text-sm text-charcoal/75 italic border border-dashed border-borderSubtle p-8 text-center">No templates yet — add your first one on the right.</p>
                )}
                {snippets.map((s) => (
                  <div key={s.id} className="border border-borderSubtle bg-white p-4 flex items-start justify-between gap-4" data-testid={`snippet-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="overline text-oxblood mb-1">{s.title}</p>
                      <p className="text-sm text-charcoal whitespace-pre-wrap leading-relaxed">{s.body}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditSnippet(s)}
                        className="text-charcoal/75 hover:text-oxblood p-1.5 transition-colors"
                        data-testid={`snippet-edit-${s.id}`}
                        aria-label={`Edit ${s.title}`}
                      >
                        <Pencil size={14}/>
                      </button>
                      <button
                        onClick={() => deleteSnippet(s.id)}
                        className="text-charcoal/75 hover:text-oxblood p-1.5 transition-colors"
                        data-testid={`snippet-delete-${s.id}`}
                        aria-label={`Delete ${s.title}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-4">{editingSnippetId ? "Edit template" : "New template"}</h3>
              <form onSubmit={saveSnippet} className="space-y-4 border border-borderSubtle bg-white p-5">
                <div>
                  <Label className="overline font-bold text-charcoal">Title</Label>
                  <Input
                    required
                    value={snippetForm.title}
                    onChange={(e) => setSnippetForm({ ...snippetForm, title: e.target.value })}
                    className={inputCls}
                    placeholder="Thanks for reordering"
                    maxLength={60}
                    data-testid="snippet-title-input"
                  />
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Body</Label>
                  <Textarea
                    required
                    value={snippetForm.body}
                    onChange={(e) => setSnippetForm({ ...snippetForm, body: e.target.value })}
                    className={`${inputCls} min-h-[120px]`}
                    placeholder="Packed with ice — refrigerate on arrival."
                    maxLength={2000}
                    data-testid="snippet-body-input"
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="submit"
                    className="py-2.5 px-6 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors"
                    data-testid="snippet-save-btn"
                  >
                    {editingSnippetId ? "Update template" : "Add template"}
                  </button>
                  {editingSnippetId && (
                    <button
                      type="button"
                      onClick={resetSnippetForm}
                      className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal"
                      data-testid="snippet-cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mailing" className="mt-8" data-testid="mailing-tab">
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-borderSubtle bg-white p-5 rounded-2xl" data-testid="mailing-stat-active">
              <p className="overline mb-2">Active</p>
              <p className="font-mono text-3xl text-oxblood">{subs.active_count}</p>
              <p className="text-[12px] uppercase tracking-widest text-charcoal/75 mt-1">Subscribers</p>
            </div>
            <div className="border border-borderSubtle bg-white p-5 rounded-2xl">
              <p className="overline mb-2">Unsubbed</p>
              <p className="font-mono text-3xl text-charcoal/75">{subs.unsubscribed_count}</p>
              <p className="text-[12px] uppercase tracking-widest text-charcoal/75 mt-1">All-time</p>
            </div>
            <div className="border border-borderSubtle bg-white p-5 rounded-2xl">
              <p className="overline mb-2">Drafts</p>
              <p className="font-mono text-3xl">{campaigns.filter((c) => c.status !== "sent").length}</p>
              <p className="text-[12px] uppercase tracking-widest text-charcoal/75 mt-1">In progress</p>
            </div>
            <div className="border border-borderSubtle bg-white p-5 rounded-2xl">
              <p className="overline mb-2">Sent</p>
              <p className="font-mono text-3xl">{campaigns.filter((c) => c.status === "sent").length}</p>
              <p className="text-[12px] uppercase tracking-widest text-charcoal/75 mt-1">Lifetime</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_1fr] gap-8">
            {/* Composer */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-serif text-2xl">{editingCampaignId ? "Edit campaign" : "New campaign"}</h3>
                {editingCampaignId && (
                  <button
                    type="button"
                    onClick={resetCampaignForm}
                    className="text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal"
                    data-testid="campaign-new-btn"
                  >
                    + New draft
                  </button>
                )}
              </div>
              <form onSubmit={saveCampaign} className="space-y-4 border border-borderSubtle bg-white p-6 rounded-2xl">
                {templates.length > 0 && (
                  <div className="pb-4 border-b border-borderSubtle" data-testid="campaign-templates-row">
                    <Label className="overline font-bold text-charcoal">Start from a template</Label>
                    <Select onValueChange={loadTemplate} disabled={templateLoading}>
                      <SelectTrigger className="mt-2 rounded-none border-borderSubtle bg-white focus:ring-0" data-testid="campaign-template-select">
                        <SelectValue placeholder={templateLoading ? "Loading…" : "Pick a template…"} />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.name} value={t.name}>
                            {t.label} — {t.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[12px] text-charcoal/75 mt-1.5">
                      Templates pull live product data (top featured kits). Edit anything after loading.
                    </p>
                  </div>
                )}
                <div>
                  <Label className="overline font-bold text-charcoal">Subject line</Label>
                  <Input
                    required
                    value={campaignForm.subject}
                    onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                    className={inputCls}
                    placeholder="Reta 10 is back — first batch in 6 weeks"
                    maxLength={200}
                    data-testid="campaign-subject-input"
                  />
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Body (HTML allowed)</Label>
                  <Textarea
                    required
                    value={campaignForm.body_html}
                    onChange={(e) => setCampaignForm({ ...campaignForm, body_html: e.target.value })}
                    className={`${inputCls} min-h-[220px] font-mono text-sm`}
                    placeholder={"<p>Hey there —</p>\n<p>Reta 10 is back in stock as of this morning. First-come first-served — <a href=\"https://bulkbuykits.com/shop/reta-10\">grab a kit here</a>.</p>"}
                    maxLength={100000}
                    data-testid="campaign-body-input"
                  />
                  <p className="text-[12px] text-charcoal/75 mt-1">
                    We'll auto-wrap this in the branded shell with your unsubscribe footer + postal address.
                  </p>
                </div>
                {/* Segment tags */}
                <div>
                  <Label className="overline font-bold text-charcoal">Segment (optional)</Label>
                  {(() => {
                    const allTags = Array.from(new Set(customers.flatMap((c) => c.tags || []))).sort();
                    if (allTags.length === 0) {
                      return <p className="text-[13px] text-charcoal/75 mt-2">No buyer tags yet — campaign will send to all active subscribers.</p>;
                    }
                    return (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="campaign-segment-tags">
                        {allTags.map((t) => {
                          const active = campaignForm.segment_tags.includes(t);
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => toggleSegmentTag(t)}
                              className={`text-[13px] uppercase tracking-[0.18em] px-3 py-1 border rounded-full transition-colors ${
                                active ? "bg-oxblood text-bone border-oxblood" : "bg-white text-charcoal border-borderSubtle hover:border-oxblood"
                              }`}
                              data-testid={`campaign-segment-${t.toLowerCase().replace(/\W+/g,'-')}`}
                            >
                              {t}
                            </button>
                          );
                        })}
                        {campaignForm.segment_tags.length === 0 && (
                          <p className="text-[13px] text-charcoal/75 self-center">No filter · sends to all {subs.active_count}</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-borderSubtle">
                  <button
                    type="submit"
                    disabled={campaignBusy}
                    className="py-2.5 px-5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                    data-testid="campaign-save-btn"
                  >
                    {editingCampaignId ? "Save draft" : "Create draft"}
                  </button>
                  <button
                    type="button"
                    onClick={testSendCampaign}
                    disabled={!editingCampaignId || campaignBusy}
                    className="py-2.5 px-5 border border-borderSubtle hover:border-oxblood text-charcoal hover:text-oxblood tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                    data-testid="campaign-test-send-btn"
                    title="Send this email to your own admin address"
                  >
                    Test send to me
                  </button>
                  <button
                    type="button"
                    onClick={sendCampaign}
                    disabled={!editingCampaignId || campaignBusy || !(campaigns.find((c) => c.id === editingCampaignId)?.test_sent_at)}
                    className="py-2.5 px-5 bg-oxblood hover:bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="campaign-send-btn"
                    title={!editingCampaignId ? "Save draft first" : !(campaigns.find((c) => c.id === editingCampaignId)?.test_sent_at) ? "Test-send to yourself first" : "Send to all subscribers"}
                  >
                    Send to all →
                  </button>
                </div>
                {editingCampaignId && !(campaigns.find((c) => c.id === editingCampaignId)?.test_sent_at) && (
                  <p className="text-[13px] text-oxblood pt-1" data-testid="campaign-test-required">
                    Test-send required before you can send live. This is a safety rail so you don't blast a broken email.
                  </p>
                )}
              </form>
            </div>

            {/* Right column: campaign list + subscribers */}
            <div className="space-y-8">
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-serif text-2xl">Campaigns</h3>
                  <button
                    type="button"
                    onClick={() => setShowSent((s) => !s)}
                    className="text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal"
                    data-testid="toggle-sent-campaigns"
                  >
                    {showSent ? "Hide sent" : "Show sent"}
                  </button>
                </div>
                <div className="space-y-2" data-testid="campaign-list">
                  {campaigns.length === 0 && (
                    <p className="text-[15px] text-charcoal/75 italic border border-dashed border-borderSubtle p-6 text-center rounded-2xl">No campaigns yet — write your first draft on the left.</p>
                  )}
                  {campaigns
                    .filter((c) => showSent || c.status !== "sent")
                    .map((c) => (
                      <div key={c.id} className={`border border-borderSubtle bg-white p-4 rounded-xl flex items-start gap-3 ${editingCampaignId === c.id ? "border-oxblood ring-1 ring-oxblood/20" : ""}`} data-testid={`campaign-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-[15px] text-charcoal truncate">{c.subject}</p>
                          <p className="text-[13px] text-charcoal/75 mt-1">
                            {c.status === "sent" ? (
                              <span className="text-oxblood">Sent · {c.recipients_count} recipients · {new Date(c.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                            ) : c.test_sent_at ? (
                              <span>Draft · test sent {new Date(c.test_sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                            ) : (
                              <span>Draft · not yet tested</span>
                            )}
                            {c.segment_tags?.length > 0 && (
                              <> · {c.segment_tags.join(", ")}</>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {c.status !== "sent" && (
                            <>
                              <button
                                onClick={() => openEditCampaign(c)}
                                className="text-charcoal/75 hover:text-oxblood p-1.5 transition-colors"
                                data-testid={`campaign-edit-${c.id}`}
                                aria-label="Edit"
                              >
                                <Pencil size={14}/>
                              </button>
                              <button
                                onClick={() => deleteCampaign(c.id)}
                                className="text-charcoal/75 hover:text-oxblood p-1.5 transition-colors"
                                data-testid={`campaign-delete-${c.id}`}
                                aria-label="Delete"
                              >
                                <Trash2 size={14}/>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Subscribers */}
              <div>
                <h3 className="font-serif text-2xl mb-3">Subscribers</h3>
                <div className="border border-borderSubtle bg-white rounded-2xl overflow-hidden" data-testid="subscribers-table">
                  {subs.subscribers.length === 0 ? (
                    <p className="text-[15px] text-charcoal/75 italic p-6 text-center">No subscribers yet.</p>
                  ) : (
                    <table className="w-full text-[15px]">
                      <thead className="bg-bone border-b border-borderSubtle">
                        <tr className="text-left">
                          <th className="overline px-4 py-3 text-[13px]">Email</th>
                          <th className="overline px-4 py-3 text-[13px]">Tags</th>
                          <th className="overline px-4 py-3 text-[13px]">Since</th>
                          <th className="overline px-4 py-3 text-[13px]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subs.subscribers.slice(0, 50).map((s) => (
                          <tr key={s.id} className="border-b border-borderSubtle last:border-0" data-testid={`subscriber-${s.id}`}>
                            <td className="px-4 py-3 font-mono text-[13px]">{s.email}</td>
                            <td className="px-4 py-3 text-[13px]">
                              {(s.tags || []).map((t) => (
                                <span key={t} className="inline-block mr-1 mb-1 px-2 py-0.5 text-[13px] uppercase tracking-widest bg-bone border border-borderSubtle rounded-full">{t}</span>
                              ))}
                            </td>
                            <td className="px-4 py-3 text-[13px] text-charcoal/75 font-mono">
                              {s.opted_in_at ? new Date(s.opted_in_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              {s.subscribed ? (
                                <span className="overline text-oxblood">Active</span>
                              ) : (
                                <span className="overline text-charcoal/75">Unsubbed</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-8" data-testid="settings-tab">
          <AdminUsername />
          <TempAccessPanel />
          <ShippingOriginPanel />
          <div className="max-w-2xl">
            <h3 className="font-serif text-2xl mb-2">Shipping</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              Set the subtotal at which USPS Priority Mail and UPS 2nd Day Air become free. SUPPLY and SELECT have independent thresholds. 10-Vial Kits (10VK) never qualify for free shipping. Changes apply the moment you save — new orders only.
            </p>
            <form onSubmit={saveShippingSettings} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5" data-testid="shipping-settings-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="overline font-bold text-charcoal">SUPPLY free-ship threshold ($)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shipDraft.free_threshold}
                      onChange={(e) => setShipDraft({ ...shipDraft, free_threshold: e.target.value })}
                      className="pl-7 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="250.00"
                      data-testid="shipping-free-threshold-input"
                    />
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Current: free over <span className="font-mono text-charcoal">${(shipCfg.free_threshold ?? 0).toFixed(2)}</span></p>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">SELECT free-ship threshold ($)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shipDraft.free_threshold_select}
                      onChange={(e) => setShipDraft({ ...shipDraft, free_threshold_select: e.target.value })}
                      className="pl-7 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="250.00"
                      data-testid="shipping-free-threshold-select-input"
                    />
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Current: free over <span className="font-mono text-charcoal">${(shipCfg.free_threshold_select ?? 0).toFixed(2)}</span></p>
                </div>
              </div>
              <p className="text-[12px] text-charcoal/60 border-t border-borderSubtle pt-4">Enforced at checkout for USPS Priority Mail and UPS 2nd Day Air. All other services (Ground, Next-Day, etc.) always paid. 10VK carts never qualify.</p>
              <div className="pt-1 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={shipBusy}
                  className="px-6 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                  data-testid="shipping-save-btn"
                >
                  {shipBusy ? "Saving…" : "Save shipping"}
                </button>
              </div>
            </form>

            <h3 className="font-serif text-2xl mt-12 mb-2">BYOK discount ladder</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              How much the buyer saves when they build a custom 3-vial or 5-vial kit in the Build-Your-Own-Kit flow. Applied server-side — buyers can't tamper. Changes take effect on new BYOK carts immediately.
            </p>
            <form onSubmit={saveByokSettings} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5" data-testid="byok-settings-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="overline font-bold text-charcoal">3-vial kit discount (%)</Label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="90"
                      value={byokDraft.discount_3vial}
                      onChange={(e) => setByokDraft({ ...byokDraft, discount_3vial: e.target.value })}
                      className="pr-8 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="10"
                      data-testid="byok-discount-3vial-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">%</span>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Percent off the summed 3 vial prices in a custom kit.</p>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">5-vial kit discount (%)</Label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="90"
                      value={byokDraft.discount_5vial}
                      onChange={(e) => setByokDraft({ ...byokDraft, discount_5vial: e.target.value })}
                      className="pr-8 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="20"
                      data-testid="byok-discount-5vial-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">%</span>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Percent off the summed 5 vial prices in a custom kit.</p>
                </div>
              </div>
              <div className="pt-3 border-t border-borderSubtle flex items-center gap-3">
                <button
                  type="submit"
                  disabled={byokBusy}
                  className="px-6 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                  data-testid="byok-save-btn"
                >
                  {byokBusy ? "Saving…" : "Save BYOK ladder"}
                </button>
                <p className="text-[13px] text-charcoal/75">
                  Current: <span className="font-mono text-charcoal">{(byokCfg.discount_3vial * 100).toFixed(1)}%</span> · 3-vial, <span className="font-mono text-charcoal">{(byokCfg.discount_5vial * 100).toFixed(1)}%</span> · 5-vial
                </p>
              </div>
            </form>

            <h3 className="font-serif text-2xl mt-12 mb-2">Select-tier vial ladder</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              How much the buyer saves when they buy 3 or 5 vials of a single Research 1617 kit. Applied server-side to the 1/3/5-Vial tile pricing on every Select product detail page. 1-Vial always pays full unit price.
            </p>
            <form onSubmit={saveSelectLadderSettings} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5" data-testid="select-ladder-settings-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="overline font-bold text-charcoal">3-vial discount (%)</Label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="90"
                      value={selectLadderDraft.discount_3vial}
                      onChange={(e) => setSelectLadderDraft({ ...selectLadderDraft, discount_3vial: e.target.value })}
                      className="pr-8 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="5"
                      data-testid="select-ladder-3vial-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">%</span>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Percent off when the buyer picks the 3-Vial tile.</p>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">5-vial discount (%)</Label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="90"
                      value={selectLadderDraft.discount_5vial}
                      onChange={(e) => setSelectLadderDraft({ ...selectLadderDraft, discount_5vial: e.target.value })}
                      className="pr-8 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="10"
                      data-testid="select-ladder-5vial-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">%</span>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Percent off when the buyer picks the 5-Vial tile.</p>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">10-vial discount (%)</Label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="90"
                      value={selectLadderDraft.discount_10vial}
                      onChange={(e) => setSelectLadderDraft({ ...selectLadderDraft, discount_10vial: e.target.value })}
                      className="pr-8 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                      placeholder="0"
                      data-testid="select-ladder-10vial-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">%</span>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-1.5">Percent off when the buyer picks the 10-Vial tile.</p>
                </div>
              </div>
              <div className="pt-3 border-t border-borderSubtle flex items-center gap-3">
                <button
                  type="submit"
                  disabled={selectLadderBusy}
                  className="px-6 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                  data-testid="select-ladder-save-btn"
                >
                  {selectLadderBusy ? "Saving…" : "Save Select ladder"}
                </button>
                <p className="text-[13px] text-charcoal/75">
                  Current: <span className="font-mono text-charcoal">{(selectLadderCfg.discount_3vial * 100).toFixed(1)}%</span> · 3-vial, <span className="font-mono text-charcoal">{(selectLadderCfg.discount_5vial * 100).toFixed(1)}%</span> · 5-vial, <span className="font-mono text-charcoal">{((selectLadderCfg.discount_10vial || 0) * 100).toFixed(1)}%</span> · 10-vial
                </p>
              </div>
            </form>

            <h3 className="font-serif text-2xl mt-12 mb-2">Landing page</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              The phone number shown on the public marketing landing page (Why-members section, "Real phone consultations."). Updates go live within a few seconds — no re-publish needed.
            </p>
            <form onSubmit={saveLandingSettings} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5" data-testid="landing-settings-form">
              <div className="max-w-xs">
                <Label className="overline font-bold text-charcoal">Consultation phone</Label>
                <Input
                  type="tel"
                  value={landingDraft}
                  onChange={(e) => setLandingDraft(e.target.value)}
                  className="mt-2 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                  placeholder="931-327-0458"
                  data-testid="landing-phone-input"
                />
                <p className="text-[12px] text-charcoal/75 mt-1.5">Displayed as-is; also wired to <code>tel:</code> for tap-to-call.</p>
              </div>
              <div className="pt-3 border-t border-borderSubtle flex items-center gap-3">
                <button
                  type="submit"
                  disabled={landingBusy}
                  className="px-6 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                  data-testid="landing-save-btn"
                >
                  {landingBusy ? "Saving…" : "Save landing"}
                </button>
                <p className="text-[13px] text-charcoal/75">
                  Current: <span className="font-mono text-charcoal">{landingCfg.consultation_phone}</span>
                </p>
              </div>
            </form>

            <h3 className="font-serif text-2xl mt-12 mb-2">Select launch ribbon</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              Show a gold "Now Live" ribbon at the top of the Research Select catalog so returning buyers immediately spot the new shelf. Flip it off once the launch feels settled.
            </p>
            <div className="border border-borderSubtle bg-white p-6 rounded-2xl flex items-center justify-between gap-4" data-testid="select-launch-ribbon-settings">
              <div>
                <p className="font-serif text-lg text-charcoal">
                  Ribbon is currently{" "}
                  <span className={landingCfg.select_now_live_enabled ? "text-charcoal" : "text-charcoal/75"}>
                    {landingCfg.select_now_live_enabled ? "showing" : "hidden"}
                  </span>.
                </p>
                <p className="text-[13px] text-charcoal/75 mt-1">
                  Renders only on the Select catalog (<code>/shop?tier=select</code>). Supply is untouched.
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleSelectNowLive(!landingCfg.select_now_live_enabled)}
                disabled={selectNowLiveBusy}
                className={`px-6 py-2.5 tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50 ${
                  landingCfg.select_now_live_enabled
                    ? "bg-oxblood hover:bg-charcoal text-bone"
                    : "bg-charcoal hover:bg-oxblood text-bone"
                }`}
                data-testid="select-launch-ribbon-toggle"
              >
                {selectNowLiveBusy ? "Saving…" : (landingCfg.select_now_live_enabled ? "Hide ribbon" : "Show ribbon")}
              </button>
            </div>

            <h3 className="font-serif text-2xl mt-12 mb-2">Select story scroll speed</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              Controls the auto-scrolling product row on the Research Select
              landing page (<code>/select-preview</code>). Pauses on hover so
              buyers can linger. Requires at least 4 active Select products.
            </p>
            <div className="border border-borderSubtle bg-white p-6 rounded-2xl flex flex-col sm:flex-row sm:items-end gap-4" data-testid="select-scroll-speed-block">
              <div className="flex-1">
                <Label className="overline font-bold text-charcoal">Speed</Label>
                <select
                  value={landingCfg.select_scroll_speed || "slow"}
                  onChange={async (e) => {
                    const speed = e.target.value;
                    try {
                      const { data } = await api.put("/admin/settings/landing", {
                        consultation_phone: landingCfg.consultation_phone || landingDraft || "931-327-0458",
                        select_scroll_speed: speed,
                      });
                      setLandingCfg(data);
                      toast.success(`Scroll speed set to ${speed}.`);
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail));
                    }
                  }}
                  className={inputCls}
                  data-testid="select-scroll-speed-select"
                >
                  <option value="glacial">Glacial — 90s per loop</option>
                  <option value="slow">Slow — 50s per loop (default)</option>
                  <option value="normal">Normal — 15s per loop</option>
                  <option value="fast">Fast — 8s per loop</option>
                </select>
                <p className="text-[13px] text-charcoal/75 mt-2">
                  Current: <span className="font-mono text-charcoal">{landingCfg.select_scroll_speed || "slow"}</span>
                </p>
              </div>
            </div>

            <h3 className="font-serif text-2xl mt-12 mb-2">Select inventory countdown</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              Two-stage buyer-facing badges on Select cards + PDPs. Blank a
              variant's <code>Vials on hand</code> field to opt that variant
              out of tracking (no badge, no auto-decrement).
            </p>
            <div className="border border-borderSubtle bg-white p-6 rounded-2xl grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="select-stock-thresholds-block">
              <div>
                <Label className="overline font-bold text-charcoal">"Limited Stock" ribbon at ≤</Label>
                <Input
                  type="number" min="0" step="1"
                  value={landingCfg.limited_stock_threshold ?? 30}
                  onChange={(e) => setLandingCfg({ ...landingCfg, limited_stock_threshold: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  className={inputCls}
                  data-testid="limited-stock-threshold-input"
                />
              </div>
              <div>
                <Label className="overline font-bold text-charcoal">"X vials left" countdown at ≤</Label>
                <Input
                  type="number" min="0" step="1"
                  value={landingCfg.low_stock_threshold ?? 20}
                  onChange={(e) => setLandingCfg({ ...landingCfg, low_stock_threshold: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  className={inputCls}
                  data-testid="low-stock-threshold-input"
                />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    setLandingBusy(true);
                    try {
                      const { data } = await api.put("/admin/settings/landing", {
                        consultation_phone: landingCfg.consultation_phone || landingDraft || "931-327-0458",
                        limited_stock_threshold: Number(landingCfg.limited_stock_threshold ?? 30),
                        low_stock_threshold: Number(landingCfg.low_stock_threshold ?? 20),
                      });
                      setLandingCfg(data);
                      toast.success("Stock thresholds updated.");
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail));
                    } finally {
                      setLandingBusy(false);
                    }
                  }}
                  disabled={landingBusy}
                  className="px-6 py-2.5 tracking-[0.22em] text-[13px] uppercase rounded-full bg-charcoal hover:bg-oxblood text-bone disabled:opacity-50"
                  data-testid="save-stock-thresholds-btn"
                >
                  {landingBusy ? "Saving…" : "Save thresholds"}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post("/admin/low-stock-digest/send-now");
                      toast.success("Digest sent (or skipped — nothing low). Check inbox + backend logs.");
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail));
                    }
                  }}
                  className="ml-3 px-6 py-2.5 tracking-[0.22em] text-[13px] uppercase rounded-full bg-white border border-charcoal hover:bg-charcoal hover:text-bone transition-colors"
                  data-testid="send-low-stock-digest-now-btn"
                >
                  Send digest now
                </button>
              </div>
            </div>

            <h3 className="font-serif text-2xl mt-12 mb-2">Public sign-up</h3>
            <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
              When closed (recommended), visitors must apply via "Request Access" on the landing page. When open, anyone can self-register at <code>/enter</code>.
            </p>
            <div className="border border-borderSubtle bg-white p-6 rounded-2xl flex items-center justify-between gap-4" data-testid="signup-settings-block">
              <div>
                <p className="font-serif text-lg text-charcoal">
                  Self-serve signup is currently{" "}
                  <span className={signupOpen ? "text-charcoal" : "text-oxblood"}>
                    {signupOpen ? "open" : "closed"}
                  </span>.
                </p>
                <p className="text-[13px] text-charcoal/75 mt-1">
                  {signupOpen
                    ? "Anyone can create an account at /enter without your approval."
                    : "The /entry page only shows sign-in. New researchers are pushed to the Request Access form."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleSignup(!signupOpen)}
                disabled={signupBusy}
                className={`shrink-0 px-5 py-2.5 rounded-full text-[13px] uppercase tracking-[0.22em] transition-colors disabled:opacity-50 ${
                  signupOpen
                    ? "bg-white border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone"
                    : "bg-charcoal text-bone hover:bg-oxblood"
                }`}
                data-testid="signup-toggle-btn"
              >
                {signupBusy ? "Saving…" : signupOpen ? "Close signup" : "Open signup"}
              </button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fulfillment" className="mt-8" data-testid="fulfillment-tab">
          <AdminFulfillment />
        </TabsContent>

        <TabsContent value="inventory" className="mt-8" data-testid="inventory-tab">
          <AdminInventory />
          <AdminInventory10VK />
        </TabsContent>

        <TabsContent value="purchase-orders" className="mt-8" data-testid="purchase-orders-tab">
          <AdminPurchaseOrders />
        </TabsContent>

        <TabsContent value="access" className="mt-8" data-testid="access-tab">
          <AdminAccessRequests />
        </TabsContent>

        <TabsContent value="redirects" className="mt-8">
          <SlugRedirects />
        </TabsContent>

        {/* iter204.1 — Suppliers directory (vendor list) */}
        <TabsContent value="suppliers-directory" className="mt-0" data-testid="suppliers-directory-tab">
          <AdminSuppliersDirectory />
        </TabsContent>

        {/* iter204 — Reports placeholder. Honest empty state; no fake metrics. */}
        <TabsContent value="reports" className="mt-0" data-testid="reports-tab">
          <div className="-mx-4 -my-4 bg-slate-100/70 min-h-[75vh]">
            <div className="px-8 py-10 max-w-[1000px] mx-auto">
              <div className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-mono mb-1">Admin › Reports</div>
              <h2 className="text-[32px] leading-tight font-bold text-slate-900 tracking-tight font-serif">Reports</h2>
              <p className="text-[14.5px] text-slate-600 mt-1.5 max-w-2xl">
                Consolidated reporting workspace. This module is not built yet — placeholder for the operational reports Greg has requested to consolidate here.
              </p>
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-[13.5px] text-slate-700">
                <b>What lives here today:</b> nothing. No mock data is displayed. Existing dashboard widgets (sales chart, weekly digest) remain available on the main Admin landing.
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl rounded-none bg-bone border-borderSubtle max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-bone pb-3 -mx-6 px-6 -mt-6 pt-6 z-10 border-b border-borderSubtle">
            <DialogTitle className="font-serif text-3xl">{editingId ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          {restoreDraft && (
            <div
              className="mt-4 border border-[#DDA94E] bg-[#FBF4E0] px-4 py-3 flex items-start gap-3 rounded-none"
              data-testid="draft-restore-banner"
            >
              <div className="flex-1 text-sm text-charcoal">
                <div className="font-semibold">Unsaved draft found</div>
                <div className="text-charcoal/75">
                  Auto-saved {(() => {
                    try { return new Date(restoreDraft.savedAt).toLocaleString(); }
                    catch { return "recently"; }
                  })()} — restore it or start fresh.
                </div>
              </div>
              <button
                type="button"
                onClick={restoreDraftNow}
                className="px-3 py-1.5 text-[13px] uppercase tracking-wider font-semibold bg-charcoal text-bone hover:bg-black transition-colors"
                data-testid="draft-restore-button"
              >
                Restore
              </button>
              <button
                type="button"
                onClick={discardDraftNow}
                className="px-3 py-1.5 text-[13px] uppercase tracking-wider font-semibold border border-charcoal text-charcoal hover:bg-charcoal hover:text-bone transition-colors"
                data-testid="draft-discard-button"
              >
                Discard
              </button>
            </div>
          )}
          <form onSubmit={is10vkNew ? save10vkProduct : saveProduct} className="grid grid-cols-2 gap-5">
            {/* 10VK PEC — Name · Strength · SKU · Price · Image · IMS Connection ·
                Visibility · Save · Delete. No supplier, no inventory inputs,
                no OOS toggle. IMS is the sole inventory authority. */}
            {is10vkNew && (
              <>
                <div className="col-span-2 flex items-baseline justify-between border-b border-charcoal/20 pb-1.5 mt-1" data-testid="tenvk-section-header">
                  <span className="overline text-charcoal font-bold tracking-[0.28em]">{editingId ? "10VK · edit product" : "10VK · add product"}</span>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-sans text-charcoal/50">10 vials/kit · direct price</span>
                </div>
                <div className="col-span-2">
                  <Label className="overline font-bold text-charcoal">Product name</Label>
                  <Input required value={productForm.name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="product-name-input"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">Strength (MG)</Label>
                  <Input type="number" step="any" min="0" value={productForm.kit_vial_strength_mg ?? ""} onChange={(e) => setProductForm({ ...productForm, kit_vial_strength_mg: e.target.value })} className={inputCls} placeholder="e.g. 10" data-testid="tenvk-strength-input"/>
                </div>
                <div>
                  <Label className="overline font-bold text-charcoal">SKU</Label>
                  <Input value={productForm.sku ?? ""} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} className={inputCls} data-testid="tenvk-sku-input"/>
                </div>
                <div className="col-span-2">
                  <Label className="overline font-bold text-charcoal">Testing status</Label>
                  <Select value={productForm.testing_status === "tested" ? "tested" : "untested"} onValueChange={(v) => setProductForm({ ...productForm, testing_status: v })}>
                    <SelectTrigger className="mt-2 rounded-none border-borderSubtle bg-white focus:ring-0" data-testid="tenvk-testing-status-select">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="untested" data-testid="tenvk-testing-status-untested">UNTESTED</SelectItem>
                      <SelectItem value="tested" data-testid="tenvk-testing-status-tested">TESTED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="overline font-bold text-charcoal">Selling price · per 10-vial kit</Label>
                  <Input required type="number" step="0.01" min="0" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} className={inputCls} data-testid="product-price-input"/>
                </div>
                <div className="col-span-2">
                  <Label className="overline font-bold text-charcoal">Product image</Label>
                  <ProductImageUploader
                    value={productForm.image_url}
                    onChange={(url) => setProductForm({ ...productForm, image_url: url })}
                  />
                </div>
                <div className="col-span-2" data-testid="tenvk-coa-section">
                  <Label className="overline font-bold text-charcoal">COA</Label>
                  {editingId && products.find((x) => x.id === editingId) ? (
                    <CoaDropzone
                      product={products.find((x) => x.id === editingId)}
                      onChange={(d) => d?.id && setProducts((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x)))}
                    />
                  ) : (
                    <p className="mt-2 text-[12px] text-charcoal/60 font-sans" data-testid="tenvk-coa-save-first">Save the product first to attach a COA.</p>
                  )}
                </div>
                <div className="col-span-2" data-testid="tenvk-ims-connection">
                  <div className="flex items-baseline justify-between">
                    <Label className="overline font-bold text-charcoal">IMS connection</Label>
                    <span className="text-[12px] font-sans text-charcoal/70" data-testid="tenvk-available-kits">
                      Available kits: <b className="text-charcoal">{tenvkLots.filter((r) => tenvkLinked.has(r.lot_id)).reduce((s, r) => s + Number(r.kits_in_stock || 0), 0)}</b>
                    </span>
                  </div>
                  <div className="mt-2 border border-charcoal/20 rounded divide-y divide-charcoal/10">
                    {tenvkLots.length === 0 && (
                      <p className="px-4 py-3 text-[12px] text-charcoal/60 font-sans" data-testid="tenvk-no-lots">No eligible 10VK IMS lots. Receive inventory in IMS first.</p>
                    )}
                    {tenvkLots.map((r) => (
                      <label key={r.lot_id} className="flex items-center justify-between gap-4 px-4 py-2.5 cursor-pointer select-none" data-testid={`tenvk-lot-row-${r.lot_id}`}>
                        <span className="flex items-center gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={tenvkLinked.has(r.lot_id)}
                            onChange={(e) => setTenvkLinked((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.lot_id); else n.delete(r.lot_id); return n; })}
                            className="h-4 w-4 accent-charcoal cursor-pointer"
                            data-testid={`tenvk-lot-checkbox-${r.lot_id}`}
                          />
                          <span className="flex flex-col min-w-0">
                            <span className="text-[13px] font-sans text-charcoal truncate">{r.name}{r.batch_number ? ` · Lot ${r.batch_number}` : ""}</span>
                            <span className="text-[11px] font-sans text-charcoal/55">Received {r.date_received ? String(r.date_received).slice(0, 10) : "—"}</span>
                          </span>
                        </span>
                        <span className="text-[13px] font-mono text-charcoal whitespace-nowrap" data-testid={`tenvk-lot-kits-${r.lot_id}`}>{Number(r.kits_in_stock || 0)} kits</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="overline font-bold text-charcoal">Visibility</Label>
                  <div className="mt-2 flex gap-2" data-testid="tenvk-visibility">
                    <button
                      type="button"
                      onClick={() => setProductForm({ ...productForm, active: true })}
                      className={`flex-1 py-2.5 text-[12px] uppercase tracking-[0.2em] border rounded-full transition-colors ${productForm.active !== false ? "bg-charcoal text-bone border-charcoal" : "border-charcoal/30 text-charcoal/70 hover:border-charcoal"}`}
                      data-testid="tenvk-visibility-active"
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => setProductForm({ ...productForm, active: false })}
                      className={`flex-1 py-2.5 text-[12px] uppercase tracking-[0.2em] border rounded-full transition-colors ${productForm.active === false ? "bg-charcoal text-bone border-charcoal" : "border-charcoal/30 text-charcoal/70 hover:border-charcoal"}`}
                      data-testid="tenvk-visibility-hidden"
                    >
                      Hidden
                    </button>
                  </div>
                </div>
                <div className="col-span-2">
                  <button type="submit" disabled={tenvkBusy} className="w-full py-3 bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase hover:bg-oxblood transition-colors rounded-full disabled:opacity-50" data-testid="save-product-btn">
                    {tenvkBusy ? "Saving…" : "Save changes"}
                  </button>
                </div>
                {editingId && (
                  <div className="col-span-2">
                    <button type="button" disabled={tenvkBusy} onClick={delete10vkProduct} className="w-full py-2.5 border border-oxblood text-oxblood tracking-[0.22em] text-[12px] uppercase hover:bg-oxblood hover:text-bone transition-colors rounded-full disabled:opacity-50" data-testid="tenvk-delete-product-btn">
                      Delete product
                    </button>
                  </div>
                )}
              </>
            )}
            {!is10vkNew && (<>
            {/* iter203.3 — Section header (Supply only). Purely visual — no
                reordering of the underlying controls. Groups the Basic
                Information fields the operator hits first. */}
            {productForm.tier === "supply" && (
              <div className="col-span-2 flex items-baseline justify-between border-b border-oxblood/20 pb-1.5 mt-1" data-testid="supply-section-basic">
                <span className="overline text-oxblood font-bold tracking-[0.28em]">Basic information</span>
                <span className="text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/50">Name · slug · category</span>
              </div>
            )}
            <div>
              <Label className="overline font-bold text-charcoal">Name</Label>
              <Input required value={productForm.name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="product-name-input"/>
            </div>
            <div>
              <Label className="overline font-bold text-charcoal">Slug <span className="text-charcoal/75 normal-case tracking-normal">(auto-filled)</span></Label>
              <Input required value={productForm.slug} onChange={(e) => { setSlugTouched(true); setProductForm({ ...productForm, slug: e.target.value }); }} className={inputCls} data-testid="product-slug-input"/>
            </div>
            <div>
              <Label className="overline font-bold text-charcoal">Category</Label>
              {(() => {
                const isKnown = productForm.category && categories.includes(productForm.category);
                const isCustom = productForm.category && !isKnown;
                return (
                  <>
                    <Select
                      value={isKnown ? productForm.category : (isCustom ? "__custom__" : "")}
                      onValueChange={(v) => {
                        if (v === "__custom__") {
                          // Reveal custom input — start with empty so user can type freely
                          setProductForm({ ...productForm, category: " " });
                        } else {
                          setProductForm({ ...productForm, category: v });
                        }
                      }}
                    >
                      <SelectTrigger className="mt-2 rounded-none border-borderSubtle bg-white focus:ring-0" data-testid="product-category-select">
                        <SelectValue placeholder="Pick or add new…" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                        <SelectItem value="__custom__" className="text-oxblood font-medium">+ New category…</SelectItem>
                      </SelectContent>
                    </Select>
                    {isCustom && (
                      <Input
                        autoFocus
                        value={productForm.category}
                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                        className={`${inputCls} mt-2`}
                        placeholder="Type new category (e.g. Peptide, Amino Compound)"
                        data-testid="product-category-input"
                      />
                    )}
                  </>
                );
              })()}
            </div>
            {/* PEC = Product information only. Inventory is managed in IMS.
                (Removed: product-level Vials on hand input — Stage 1 authority repair.) */}
            <div>
              <Label className="overline font-bold text-charcoal">Max qty per order</Label>
              <Input
                type="number"
                min="1"
                placeholder="Leave blank for no limit"
                value={productForm.max_qty_per_order ?? ""}
                onChange={(e) => setProductForm({ ...productForm, max_qty_per_order: e.target.value })}
                className={inputCls}
                data-testid="product-max-qty-input"
              />
              <p className="text-[13px] text-charcoal/75 mt-1">
                {productForm.max_qty_per_order
                  ? `Buyers can add up to ${productForm.max_qty_per_order} per order.`
                  : "No per-order limit set."}
              </p>
            </div>

            {/* Tier badge (read-only — controlled by the Supply/Select sub-tab at the top of the Products page) */}
            <div className="col-span-2">
              <Label className="overline font-bold text-charcoal">Catalog tier</Label>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className={`inline-flex items-center text-[12px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full ${
                    productForm.tier === "select"
                      ? "bg-black text-[#DDA94E] border border-[#DDA94E]"
                      : "bg-bone text-charcoal border border-charcoal"
                  }`}
                  data-testid="product-tier-badge"
                >
                  {productForm.tier === "select" ? "Research Select (R-1617)" : "Research Supply"}
                </span>
                <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono">
                  Set by the sub-tab above · use “Duplicate to other tier” to copy this product across
                </p>
              </div>
            </div>

            {/* iter184 — Supply Fixed-Tier Pricing controls. Only visible on
                 Supply-tier products. Assigns the product to a $-tier and
                 captures the kit vial count + mg-per-vial strength. */}
            {productForm.tier === "supply" && (
              <div className="col-span-2 flex items-baseline justify-between border-b border-oxblood/20 pb-1.5 mt-2" data-testid="supply-section-config">
                <span className="overline text-oxblood font-bold tracking-[0.28em]">Supply configuration</span>
                <span className="text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/50">3-vial price · 10-vial price · strengths</span>
              </div>
            )}
            {productForm.tier === "supply" && (
              <div className="col-span-2 rounded-2xl border border-oxblood/20 p-4 bg-oxblood/5" data-testid="supply-kit-pricing-card">
                {!(Number(productForm.price) > 0) && (productForm.variants || []).length === 0 && (
                  <div
                    className="mb-3 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-[13px] text-amber-900"
                    data-testid="supply-price-required-note"
                  >
                    <span className="font-semibold">Enter a 3-vial kit price before publishing.</span>
                  </div>
                )}
                {(productForm.variants || []).length > 0 ? (
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono" data-testid="supply-prices-per-strength-note">
                    3-vial and 10-vial prices are set per strength below.
                  </p>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/80">3-Vial Kit Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={productForm.price ?? ""}
                      onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                      className={inputCls}
                      placeholder="0.00"
                      data-testid="supply-price-3vial-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/80">10-Vial Kit Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={productForm.price_10vial ?? ""}
                      onChange={(e) => setProductForm({ ...productForm, price_10vial: e.target.value === "" ? null : e.target.value })}
                      className={inputCls}
                      placeholder="Leave blank to hide 10-vial kit"
                      data-testid="supply-price-10vial-input"
                    />
                  </div>
                </div>
                )}
              </div>
            )}

            {productForm.tier === "supply" && (
              <div className="col-span-2 flex items-baseline justify-between border-b border-oxblood/20 pb-1.5 mt-2" data-testid="supply-section-inventory">
                <span className="overline text-oxblood font-bold tracking-[0.28em]">Inventory &amp; availability</span>
                <span className="text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/50">Live · stock · featured</span>
              </div>
            )}

            {/* Pricing model picker — the radio drives which pricing UI
                shows below. Hidden on ALL Supply-tier products (iter203.4):
                Supply pricing is authoritative through Assigned Tier · Vials
                Per Kit · MG Per Vial in Supply Configuration. A tier-less
                Supply row is INCOMPLETE, not a legacy pricing branch — the
                operator is prompted to assign a tier via the warning inside
                the Supply Price Tier card. Select retains the picker. */}
            {productForm.tier === "select" && (
            <div className="col-span-2 rounded-2xl border border-borderSubtle p-4 bg-bone/60" data-testid="pricing-model-picker">
              <Label className="overline font-bold text-charcoal">Pricing model</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {productForm.tier === "select" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setPricingModelAndClean("single-vial")}
                      className={`text-[13px] uppercase tracking-[0.22em] font-mono px-3 py-2 rounded-full border transition-colors ${
                        pricingModel === "single-vial"
                          ? "bg-oxblood text-bone border-oxblood"
                          : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
                      }`}
                      data-testid="pricing-model-single-vial"
                    >
                      Single Vial Pricing
                    </button>
                    <button
                      type="button"
                      onClick={() => setPricingModelAndClean("multi-mg")}
                      className={`text-[13px] uppercase tracking-[0.22em] font-mono px-3 py-2 rounded-full border transition-colors ${
                        pricingModel === "multi-mg"
                          ? "bg-oxblood text-bone border-oxblood"
                          : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
                      }`}
                      data-testid="pricing-model-multi-mg"
                    >
                      Multi-MG Variants
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setPricingModelAndClean("single-price")}
                      className={`text-[13px] uppercase tracking-[0.22em] font-mono px-3 py-2 rounded-full border transition-colors ${
                        pricingModel === "single-price"
                          ? "bg-oxblood text-bone border-oxblood"
                          : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
                      }`}
                      data-testid="pricing-model-single-price"
                    >
                      Single Price
                    </button>
                    <button
                      type="button"
                      onClick={() => setPricingModelAndClean("multi-mg")}
                      className={`text-[13px] uppercase tracking-[0.22em] font-mono px-3 py-2 rounded-full border transition-colors ${
                        pricingModel === "multi-mg"
                          ? "bg-oxblood text-bone border-oxblood"
                          : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
                      }`}
                      data-testid="pricing-model-multi-mg"
                    >
                      Multi-MG Variants
                    </button>
                  </>
                )}
              </div>
              <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 mt-2 font-mono">
                {pricingModel === "single-price" && "One flat price per kit. Buyers pick quantity."}
                {pricingModel === "single-vial" && "One per-vial price. Storefront auto-prices the 1/3/5-vial tiles using the Select ladder in Settings."}
                {pricingModel === "multi-mg" && "Buyers see an mg-strength dropdown, each with its own per-vial price."}
              </p>
            </div>
            )}

            {/* Flat price — hidden on ALL Supply products (iter203.4).
                Multi-mg products carry their prices in variants[]; single-
                strength Supply prices via Assigned Tier. */}
            {(pricingModel === "single-price" || pricingModel === "single-vial") && productForm.tier === "select" && (
              <div className="col-span-2">
                <Label className="overline font-bold text-charcoal">
                  {pricingModel === "single-vial" ? "Price per vial" : "Price per kit"}
                </Label>
                <Input required type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} className={inputCls} data-testid="product-price-input"/>
                {pricingModel === "single-vial" && (
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 mt-1.5 font-mono">
                    3-Vial and 5-Vial tile prices are auto-calculated on the storefront from the ladder in Admin → Settings.
                  </p>
                )}
              </div>
            )}

            {/* mg-per-vial variants — Multi-MG mode only. Hidden on ALL
                Supply-tier products (iter203.4): tier-bound Supply is single-
                strength via the iter203.2 bridge, and a tier-less Supply row
                is incomplete rather than a legacy multi-mg fallback. Select
                and any future non-Supply/non-Select tier retain the editor
                unchanged. */}
            {((pricingModel === "multi-mg" && productForm.tier === "select") || productForm.tier === "supply") && (
            <div className="col-span-2" data-testid="mg-variants-editor">
              <div className="flex items-center justify-between mb-2">
                <Label className="overline font-bold text-charcoal">
                  Variant strengths
                </Label>
                <div className="flex items-center gap-2">
                  {/* iter148w — Unit picker. Applied to every variant row for this product. */}
                  <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-charcoal/60 mr-1">Unit</span>
                  {["mg", "ml", "mcg", "iu"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setProductForm({ ...productForm, variant_unit: u })}
                      className={`text-[11px] uppercase tracking-[0.22em] font-mono px-3 py-1.5 rounded-full border transition-colors ${
                        (productForm.variant_unit || "mg") === u
                          ? "bg-charcoal text-bone border-charcoal"
                          : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
                      }`}
                      data-testid={`variant-unit-${u}`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setProductForm({
                    ...productForm,
                    variants: [
                      ...(productForm.variants || []),
                      productForm.tier === "supply"
                        ? { mg: "", price: "", price_10vial: "", in_stock: true }
                        : { mg: "", price: "", in_stock: true },
                    ],
                  })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-borderSubtle bg-white px-3 py-1.5 text-[12px] uppercase tracking-[0.22em] hover:bg-charcoal hover:text-bone transition-colors font-mono"
                  data-testid="add-mg-variant-btn"
                >
                  + Add option
                </button>
              </div>
              <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 mb-3 font-mono">
                Buyers pick one strength on the product page. Enter numbers only — the {(productForm.variant_unit || "mg").toUpperCase()} unit above is auto-applied.{productForm.tier === "select" ? " Select-tier PDPs auto-price 3-vial and 5-vial tiles via the ladder in Settings." : ""}
              </p>
              {(productForm.variants || []).length === 0 ? (
                <div className="border border-dashed border-borderSubtle rounded-lg p-4 bg-bone/40" data-testid="mg-variants-empty">
                  <p className="text-[13px] text-charcoal font-medium mb-1">
                    Click "+ Add option" above to add mg variants.
                  </p>
                  <p className="text-[13px] text-charcoal/75 italic">
                    Otherwise this product uses the flat price above.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {productForm.tier === "supply" && (
                    <div className="grid gap-2 grid-cols-[1fr_1fr_1fr_auto] text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/70" data-testid="supply-variant-headers">
                      <span>{(productForm.variant_unit || "mg").toUpperCase()}</span><span>3-vial kit $</span><span>10-vial kit $</span><span className="w-[30px]" />
                    </div>
                  )}
                  {(productForm.variants || []).map((v, idx) => (
                    <div
                      key={idx}
                      className={`grid gap-2 items-center ${productForm.tier === "supply" ? "grid-cols-[1fr_1fr_1fr_auto]" : "grid-cols-[1fr_1fr_auto]"}`}
                      data-testid={`mg-variant-row-${idx}`}
                    >
                      <Input
                        type="text"
                        placeholder={`Amount (e.g. 5, 10, 30) — ${(productForm.variant_unit || "mg")} auto-added`}
                        value={v.mg ?? ""}
                        onChange={(e) => {
                          const next = [...(productForm.variants || [])];
                          next[idx] = { ...next[idx], mg: e.target.value };
                          setProductForm({ ...productForm, variants: next });
                        }}
                        className={inputCls}
                        data-testid={`mg-variant-mg-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={productForm.tier === "supply" ? "3-vial kit price" : "Price"}
                        value={v.price ?? ""}
                        onChange={(e) => {
                          const next = [...(productForm.variants || [])];
                          next[idx] = { ...next[idx], price: e.target.value };
                          setProductForm({ ...productForm, variants: next });
                        }}
                        className={inputCls}
                        data-testid={`mg-variant-price-${idx}`}
                      />
                      {productForm.tier === "supply" && (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="10-vial kit price"
                          value={v.price_10vial ?? ""}
                          onChange={(e) => {
                            const next = [...(productForm.variants || [])];
                            next[idx] = { ...next[idx], price_10vial: e.target.value };
                            setProductForm({ ...productForm, variants: next });
                          }}
                          className={inputCls}
                          data-testid={`mg-variant-price10-${idx}`}
                        />
                      )}
                      {/* PEC = Product information only. Per-mg inventory is managed in IMS.
                          (Removed: per-variant Vials on hand input — Stage 1 authority repair.) */}
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...(productForm.variants || [])];
                          next.splice(idx, 1);
                          setProductForm({ ...productForm, variants: next });
                        }}
                        className="text-charcoal/75 hover:text-oxblood p-2 transition-colors"
                        data-testid={`mg-variant-remove-${idx}`}
                        aria-label="Remove"
                      >
                        <Trash2 size={14} strokeWidth={1.5}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            <div className="col-span-2 flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => setProductForm({ ...productForm, active: !productForm.active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${!productForm.active ? "bg-oxblood" : "bg-borderSubtle"}`}
                data-testid="product-pause-toggle"
                aria-pressed={!productForm.active}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${!productForm.active ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
              <div>
                <p className="text-sm font-medium">{!productForm.active ? "Paused" : "Live"}</p>
                <p className="text-[13px] text-charcoal/75">
                  {!productForm.active
                    ? "Hidden from buyers on both tiers. Not deleted — flip off to bring back."
                    : "Toggle on to temporarily hide this kit from buyers without deleting it."}
                </p>
              </div>
            </div>
            <div className="col-span-2 flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => setProductForm({ ...productForm, featured: !productForm.featured })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${productForm.featured ? "bg-oxblood" : "bg-borderSubtle"}`}
                data-testid="product-featured-toggle"
                aria-pressed={productForm.featured}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${productForm.featured ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
              <div>
                <p className="text-sm font-medium">{productForm.featured ? "Featured" : "Not featured"}</p>
                <p className="text-[13px] text-charcoal/75">
                  {productForm.featured
                    ? "This kit gets the hero spotlight at the top of the catalog. Only one can be featured at a time."
                    : "Toggle on to spotlight this kit at the top of the catalog."}
                </p>
              </div>
            </div>
            {productForm.tier === "select" && (
              <div className="col-span-2 flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setProductForm({ ...productForm, is_lyo_usa: !productForm.is_lyo_usa })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${productForm.is_lyo_usa ? "bg-oxblood" : "bg-borderSubtle"}`}
                  data-testid="product-lyo-usa-toggle"
                  aria-pressed={!!productForm.is_lyo_usa}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${productForm.is_lyo_usa ? "translate-x-6" : "translate-x-1"}`}/>
                </button>
                <div>
                  <p className="text-sm font-medium">
                    {productForm.is_lyo_usa ? "Lyophilized in USA" : "Not marked as US-lyophilized"}
                  </p>
                  <p className="text-[13px] text-charcoal/75">
                    {productForm.is_lyo_usa
                      ? "Shows a US flag badge on the product card. Hover reveals: “US Lyophilization”."
                      : "Toggle on to mark this kit as lyophilized in the USA."}
                  </p>
                </div>
              </div>
            )}
            <div className="col-span-2 flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => setProductForm({ ...productForm, is_hot_seller: !productForm.is_hot_seller })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${productForm.is_hot_seller ? "bg-oxblood" : "bg-borderSubtle"}`}
                data-testid="product-hot-seller-toggle"
                aria-pressed={!!productForm.is_hot_seller}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${productForm.is_hot_seller ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
              <div>
                <p className="text-sm font-medium">{productForm.is_hot_seller ? "Hot seller" : "Not a hot seller"}</p>
                <p className="text-[13px] text-charcoal/75">
                  {productForm.is_hot_seller
                    ? "Shows a 🔥 “HOT SELLER!” badge on the product card."
                    : "Toggle on to spotlight this kit as a HOT SELLER!"}
                </p>
              </div>
            </div>
            <div className="col-span-2 flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => setProductForm({ ...productForm, single_buy: !productForm.single_buy })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${productForm.single_buy ? "bg-oxblood" : "bg-borderSubtle"}`}
                data-testid="product-single-buy-toggle"
                aria-pressed={!!productForm.single_buy}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${productForm.single_buy ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
              <div>
                <p className="text-sm font-medium">
                  {productForm.single_buy ? "Single-unit purchase allowed" : "Follows order minimum"}
                </p>
                <p className="text-[13px] text-charcoal/75">
                  {productForm.single_buy
                    ? "Buyers can order 1 unit without meeting the order minimum. Perfect for consumables like bacteriostatic water."
                    : "Toggle on to exempt this product from the tier's minimum-kit rule."}
                </p>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="overline font-bold text-charcoal">Product image</Label>
              <ProductImageUploader
                value={productForm.image_url}
                onChange={(url) => setProductForm({ ...productForm, image_url: url })}
              />
            </div>
            {productForm.tier === "select" && (
              <div className="col-span-2 pt-2 border-t border-stone/20">
                <div className="flex items-center justify-between mb-2">
                  <Label className="overline font-bold text-charcoal">
                    Select hero image <span className="text-[12px] font-normal text-charcoal/75 normal-case tracking-normal ml-1">— Optional. Editorial photo used on the Select PDP hero slot (4:5 portrait works best). Falls back to product image when empty.</span>
                  </Label>
                </div>
                <ProductImageUploader
                  value={productForm.hero_image_url}
                  onChange={(url) => setProductForm({ ...productForm, hero_image_url: url })}
                />
              </div>
            )}
            {productForm.tier === "select" && (
              <div className="col-span-2 pt-3 border-t border-stone/20">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="byok-eligible-toggle">
                  <input
                    type="checkbox"
                    checked={!!productForm.byok_eligible}
                    onChange={(e) => setProductForm({ ...productForm, byok_eligible: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-oxblood"
                    data-testid="byok-eligible-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium">Include in Build-Your-Own-Kit</p>
                    <p className="text-[13px] text-charcoal/75">
                      When on, buyers can pick this product's vials in the R-1617 custom-kit builder. Products with mg-variants expose every in-stock variant; products without variants use the base price.
                    </p>
                  </div>
                </label>
              </div>
            )}
            {productForm.tier === "select" && (
              <div className="col-span-2 pt-3 border-t border-stone/20" data-testid="select-ladder-override-block">
                <Label className="overline font-bold text-charcoal block mb-2">Select ladder override (this product only)</Label>
                <p className="text-[13px] text-charcoal/75 mb-3">
                  Leave blank to use the global Select ladder from Settings. Enter a whole-number % (0–90) to give this SKU a deeper (or shallower) discount than the site-wide default.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[13px] uppercase tracking-[0.15em] text-charcoal/75">3-Vial % off</Label>
                    <input
                      type="number"
                      min="0" max="90" step="1"
                      placeholder="use global"
                      value={productForm.select_ladder_override_3vial == null ? "" : Math.round(Number(productForm.select_ladder_override_3vial) * 100)}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        setProductForm({ ...productForm, select_ladder_override_3vial: raw === "" ? null : Math.max(0, Math.min(90, Number(raw))) / 100 });
                      }}
                      className={inputCls}
                      data-testid="select-override-3vial-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[13px] uppercase tracking-[0.15em] text-charcoal/75">5-Vial % off</Label>
                    <input
                      type="number"
                      min="0" max="90" step="1"
                      placeholder="use global"
                      value={productForm.select_ladder_override_5vial == null ? "" : Math.round(Number(productForm.select_ladder_override_5vial) * 100)}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        setProductForm({ ...productForm, select_ladder_override_5vial: raw === "" ? null : Math.max(0, Math.min(90, Number(raw))) / 100 });
                      }}
                      className={inputCls}
                      data-testid="select-override-5vial-input"
                    />
                  </div>
                </div>
              </div>
            )}
            {/* iter203.3 — Section header (Supply only). Groups the AI
                description + molecule story text. Existing "AI generate"
                behavior + backend endpoint preserved verbatim. */}
            {productForm.tier === "supply" && (
              <div className="col-span-2 flex items-baseline justify-between border-b border-oxblood/20 pb-1.5 mt-2" data-testid="supply-section-molecule">
                <span className="overline text-oxblood font-bold tracking-[0.28em]">Molecule information</span>
                <span className="text-[11px] uppercase tracking-[0.22em] font-mono text-charcoal/50">Public description · AI-generated · editable</span>
              </div>
            )}
            <div className="col-span-2 flex items-center justify-between">
              <Label className="overline font-bold text-charcoal">Description</Label>
              <button
                type="button"
                onClick={generateDescription}
                disabled={genBusy || !productForm.name?.trim()}
                className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] text-oxblood hover:text-charcoal disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="ai-generate-desc-btn"
              >
                <Sparkles size={12} strokeWidth={1.75}/>
                {genBusy ? "Generating…" : "AI generate"}
              </button>
            </div>
            <div className="col-span-2 -mt-2">
              <Textarea rows={3} value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className={inputCls} data-testid="product-desc-input"/>
            </div>
            {productForm.tier === "supply" && (
              <div className="col-span-2 flex items-baseline justify-between border-b border-oxblood/20 pb-1.5 mt-2" data-testid="supply-section-spec">
                <span className="overline text-oxblood font-bold tracking-[0.28em]">Spec sheet</span>
              </div>
            )}
            {/* iter210.6 — Blend architecture. Operator MUST explicitly enable
                Blend mode. No name/category inference. When Blend=true, the
                carton back renders BLEND COMPOSITION from components[] instead
                of the technical single-compound layout. */}
            {productForm.tier === "supply" && (
              <div className="col-span-2 border rounded-md p-3" style={{ borderColor: "rgba(74,14,23,0.25)", background: "rgba(74,14,23,0.03)" }} data-testid="blend-section">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setProductForm({ ...productForm, is_blend: !productForm.is_blend })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${productForm.is_blend ? "bg-oxblood" : "bg-borderSubtle"}`}
                    data-testid="blend-toggle"
                    aria-pressed={!!productForm.is_blend}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${productForm.is_blend ? "translate-x-6" : "translate-x-1"}`}/>
                  </button>
                  <div>
                    <p className="text-sm font-medium">{productForm.is_blend ? "Blend — carton back shows BLEND COMPOSITION" : "Single compound — carton back shows TECHNICAL INFORMATION"}</p>
                    <p className="text-[13px] text-charcoal/75">Explicit operator selection. No name/category inference. A blend requires at least one component.</p>
                  </div>
                </div>
                {productForm.is_blend && (
                  <div className="mt-3 space-y-2" data-testid="components-editor">
                    <Label className="overline font-bold text-charcoal block">Blend components</Label>
                    {(productForm.components || []).map((row, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`component-row-${idx}`}>
                        <Input
                          value={row?.name || ""}
                          onChange={(e) => {
                            const next = [...(productForm.components || [])];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setProductForm({ ...productForm, components: next });
                          }}
                          placeholder="Compound name (e.g. BPC-157)"
                          className={inputCls}
                          data-testid={`component-name-${idx}`}
                        />
                        <Input
                          value={row?.mg || ""}
                          onChange={(e) => {
                            const next = [...(productForm.components || [])];
                            next[idx] = { ...next[idx], mg: e.target.value };
                            setProductForm({ ...productForm, components: next });
                          }}
                          placeholder="mg (optional)"
                          className={`${inputCls} !w-32`}
                          data-testid={`component-mg-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...(productForm.components || [])];
                            next.splice(idx, 1);
                            setProductForm({ ...productForm, components: next });
                          }}
                          className="px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-oxblood hover:text-charcoal"
                          data-testid={`component-remove-${idx}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setProductForm({ ...productForm, components: [...(productForm.components || []), { name: "", mg: "" }] })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-oxblood/40 text-[11px] uppercase tracking-[0.18em] text-oxblood hover:bg-oxblood/5 rounded-full"
                      data-testid="add-component-btn"
                    >
                      + Add component
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="col-span-2">
              <Label className="overline font-bold text-charcoal block mb-2">Spec sheet (molecule facts only — no purity, no vials/kits)</Label>
              <div className="space-y-2 mt-2" data-testid="spec-editor">
                {(productForm.spec || []).map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`spec-row-${idx}`}>
                    <Input
                      value={row?.key || ""}
                      placeholder="Key (e.g. Molecular formula)"
                      onChange={(e) => {
                        const next = [...(productForm.spec || [])];
                        next[idx] = { ...next[idx], key: e.target.value };
                        setProductForm({ ...productForm, spec: next });
                      }}
                      className={`${inputCls} flex-1`}
                      data-testid={`spec-key-${idx}`}
                    />
                    <Input
                      value={row?.value || ""}
                      placeholder="Value (e.g. C62H98N16O22)"
                      onChange={(e) => {
                        const next = [...(productForm.spec || [])];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setProductForm({ ...productForm, spec: next });
                      }}
                      className={`${inputCls} flex-[2]`}
                      data-testid={`spec-value-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = [...(productForm.spec || [])];
                        next.splice(idx, 1);
                        setProductForm({ ...productForm, spec: next });
                      }}
                      className="text-oxblood text-[13px] uppercase tracking-[0.2em] hover:text-charcoal transition-colors px-2"
                      aria-label="Remove row"
                      data-testid={`spec-remove-${idx}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setProductForm({ ...productForm, spec: [...(productForm.spec || []), { key: "", value: "" }] })}
                    className="text-[12px] uppercase tracking-[0.22em] text-oxblood hover:text-charcoal transition-colors"
                    data-testid="spec-add-row"
                  >
                    + Add spec row
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Injects the 5 standard peptide spec labels. Skips any label
                      // already present (case-insensitive) so multiple clicks don't
                      // duplicate rows on a partially-filled product.
                      const TEMPLATE = ["Molecule Class", "Amino Acids", "Molecular Formula", "Molecular Weight", "CAS Number"];
                      const existing = (productForm.spec || []).map((s) => String(s?.key || "").trim().toLowerCase());
                      const additions = TEMPLATE.filter((k) => !existing.includes(k.toLowerCase())).map((k) => ({ key: k, value: "" }));
                      if (additions.length === 0) {
                        toast.info("Spec template rows already present");
                        return;
                      }
                      setProductForm({ ...productForm, spec: [...(productForm.spec || []), ...additions] });
                    }}
                    className="text-[12px] uppercase tracking-[0.22em] text-charcoal border border-charcoal rounded-full px-3 py-1 hover:bg-charcoal hover:text-bone transition-colors font-mono"
                    data-testid="spec-load-template"
                    title="Add the 5 standard peptide spec labels (Molecule Class, Amino Acids, Molecular Formula, Molecular Weight, CAS Number)"
                  >
                    Load spec template
                  </button>
                </div>
              </div>
            </div>
            {/* Related Products curator — Select-tier only. Admin picks up
                to 4 products to surface in the PDP "You may also like"
                rail. Manual curation only (no AI, no "frequently bought
                together" logic) per 2026-02-16 site-optimization spec. */}
            {productForm.tier === "select" && (
              <div className="col-span-2 pt-3 border-t border-stone/20" data-testid="related-products-curator">
                <Label className="overline font-bold text-charcoal block mb-2">Related products (max 4)</Label>
                <p className="text-[13px] text-charcoal/75 mb-3">
                  Manual curation for the PDP "You may also like" rail. Pick up to 4 Select-tier products. Tap to add/remove — display order matches selection order.
                </p>
                {(() => {
                  const selectedIds = Array.isArray(productForm.related_product_ids)
                    ? productForm.related_product_ids
                    : [];
                  const candidates = (products || []).filter(
                    (p) => p.tier === "select" && p.id !== editingId && !p.is_draft
                  );
                  const toggle = (pid) => {
                    const cur = [...selectedIds];
                    const idx = cur.indexOf(pid);
                    if (idx >= 0) {
                      cur.splice(idx, 1);
                    } else {
                      if (cur.length >= 4) return;
                      cur.push(pid);
                    }
                    setProductForm({ ...productForm, related_product_ids: cur });
                  };
                  return (
                    <>
                      <p className="text-[12px] font-mono uppercase tracking-[0.22em] text-charcoal/60 mb-2" data-testid="related-count-hint">
                        Selected · {selectedIds.length} / 4
                      </p>
                      <div className="max-h-56 overflow-y-auto border border-borderSubtle rounded-md divide-y divide-borderSubtle bg-white">
                        {candidates.length === 0 ? (
                          <p className="p-3 text-[13px] text-charcoal/60">No other Select products available.</p>
                        ) : (
                          candidates.map((p) => {
                            const isOn = selectedIds.includes(p.id);
                            const isMax = !isOn && selectedIds.length >= 4;
                            return (
                              <label
                                key={p.id}
                                className={`flex items-center gap-3 p-2.5 cursor-pointer transition-colors ${
                                  isMax ? "opacity-50 cursor-not-allowed" : "hover:bg-bone"
                                } ${isOn ? "bg-oxblood/[0.04]" : ""}`}
                                data-testid={`related-toggle-${p.slug}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  disabled={isMax}
                                  onChange={() => toggle(p.id)}
                                  className="w-4 h-4 accent-oxblood"
                                  data-testid={`related-checkbox-${p.slug}`}
                                />
                                <span className="flex-1 text-[13px] text-charcoal">{p.name}</span>
                                {isOn && (
                                  <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-oxblood">
                                    · #{selectedIds.indexOf(p.id) + 1}
                                  </span>
                                )}
                              </label>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {/* Admin-only notes — collapsible at the bottom of the edit card
                so it doesn't visually crowd the day-to-day product fields.
                Persisted server-side on the product doc but never rendered to
                buyers (backend scrubs the field for non-admin users). */}
            <div className="col-span-2" data-testid="admin-notes-section">
              <button
                type="button"
                onClick={() => setAdminNotesOpen((v) => !v)}
                className="w-full flex items-center justify-between text-[12px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal transition-colors py-2 border-t border-borderSubtle"
                data-testid="admin-notes-toggle"
                aria-expanded={adminNotesOpen}
              >
                <span className="font-mono">
                  Admin notes {(productForm.admin_notes || "").trim() ? "· filled" : "· empty"}
                </span>
                <span aria-hidden>{adminNotesOpen ? "▾" : "▸"}</span>
              </button>
              {adminNotesOpen && (
                <div className="mt-2">
                  <Textarea
                    rows={4}
                    value={productForm.admin_notes || ""}
                    onChange={(e) => setProductForm({ ...productForm, admin_notes: e.target.value })}
                    className={inputCls}
                    placeholder="Private notes — supplier quirks, purity batch anomalies, restock cadence, anything you want to remember. Never shown to buyers."
                    data-testid="admin-notes-textarea"
                  />
                </div>
              )}
            </div>
            <div className="col-span-2">
              <button type="submit" className="w-full py-3 bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase hover:bg-oxblood transition-colors rounded-full" data-testid="save-product-btn">
                {editingId ? "Save changes" : "Create product"}
              </button>
            </div>
            </>)}
          </form>
        </DialogContent>
      </Dialog>

      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={load}
      />

      <VariantsBulkImportDialog
        open={variantsImportOpen}
        onOpenChange={setVariantsImportOpen}
        onImported={load}
      />

      <DuplicateMergeDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        onMerged={load}
      />

      <ManualInvoiceDialog
        open={manualInvoiceOpen && !manualMinimized}
        onOpenChange={(o) => {
          if (!o) {
            // Radix close (Esc / X / click outside) — discard the draft too.
            manualDraftRef.current = null;
            setManualMinimized(false);
            setManualInvoiceOpen(false);
          } else {
            setManualInvoiceOpen(true);
          }
        }}
        onCreated={load}
        onMinimize={() => setManualMinimized(true)}
        draftRef={manualDraftRef}
      />

      <EditOrderDialog
        open={!!editingOrder && !editMinimized}
        onOpenChange={(o) => {
          if (!o) {
            editDraftRef.current = null;
            setEditMinimized(false);
            setEditingOrder(null);
            setMinimizedEditOrder(null);
          }
        }}
        order={editingOrder}
        onSaved={load}
        onMinimize={() => {
          setMinimizedEditOrder(editingOrder);
          setEditMinimized(true);
        }}
        draftRef={editDraftRef}
      />

      {/* iter_133 — minimized draft chips (bottom-right, click to expand) */}
      {(manualMinimized || editMinimized) && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2" data-testid="minimized-drafts">
          {manualMinimized && (
            <div className="flex items-center gap-3 bg-charcoal text-bone px-4 py-3 border border-oxblood shadow-lg rounded-full" data-testid="manual-chip">
              <FileText size={14} strokeWidth={2}/>
              <button
                onClick={() => setManualMinimized(false)}
                className="text-xs uppercase tracking-[0.22em] hover:text-oxblood transition-colors"
                data-testid="manual-chip-expand"
              >
                Manual order draft — Expand
              </button>
              <button
                onClick={() => {
                  manualDraftRef.current = null;
                  setManualMinimized(false);
                  setManualInvoiceOpen(false);
                }}
                className="text-bone/70 hover:text-bone p-1"
                data-testid="manual-chip-discard"
                title="Discard draft"
                aria-label="Discard manual order draft"
              >
                <X size={12} strokeWidth={2}/>
              </button>
            </div>
          )}
          {editMinimized && (
            <div className="flex items-center gap-3 bg-charcoal text-bone px-4 py-3 border border-oxblood shadow-lg rounded-full" data-testid="edit-chip">
              <Pencil size={14} strokeWidth={2}/>
              <button
                onClick={() => {
                  if (minimizedEditOrder && !editingOrder) setEditingOrder(minimizedEditOrder);
                  setEditMinimized(false);
                }}
                className="text-xs uppercase tracking-[0.22em] hover:text-oxblood transition-colors"
                data-testid="edit-chip-expand"
              >
                Editing {minimizedEditOrder?.order_number || "order"} — Expand
              </button>
              <button
                onClick={() => {
                  editDraftRef.current = null;
                  setEditMinimized(false);
                  setEditingOrder(null);
                  setMinimizedEditOrder(null);
                }}
                className="text-bone/70 hover:text-bone p-1"
                data-testid="edit-chip-discard"
                title="Discard edits"
                aria-label="Discard edit draft"
              >
                <X size={12} strokeWidth={2}/>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Order note-to-buyer dialog */}
      {/* View full order (read-only) — iter120 */}
      <Dialog open={!!viewingOrder} onOpenChange={(o) => { if (!o) setViewingOrder(null); }}>
        <DialogContent className="rounded-2xl max-w-2xl bg-bone border-borderSubtle max-h-[85vh] overflow-y-auto" data-testid="admin-view-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Order #{viewingOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-6 text-[15px]" data-testid="admin-view-order-body">
              {/* Header meta */}
              <div className="flex flex-wrap items-center gap-3 -mt-2">
                <span className={`inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full font-mono border ${
                  viewingOrder.status === "paid" ? "bg-mossBg text-moss border-mossBorder" :
                  viewingOrder.status === "shipped" ? "bg-charcoal text-bone border-charcoal" :
                  viewingOrder.status === "delivered" ? "bg-oxblood text-bone border-oxblood" :
                  viewingOrder.status === "cancelled" ? "bg-charcoal/5 text-charcoal/70 border-borderSubtle" :
                  "bg-clayBg text-clay border-clayBorder"
                }`}>{viewingOrder.status}</span>
                <span className="text-[13px] text-charcoal/75 font-mono">
                  {viewingOrder.created_at ? new Date(viewingOrder.created_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                </span>
                <span className="text-[13px] text-charcoal/75 font-mono uppercase tracking-widest">
                  via {viewingOrder.payment_method}
                </span>
                {/* iter156.4 — fulfillment badge: Local Delivery (Nashville drop-off)
                    vs Pickup (out-of-area buyer, driving in). None for carrier ship. */}
                {viewingOrder.local_delivery && (
                  <span
                    className={`inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full font-mono border ${
                      viewingOrder.local_delivery_mode === "pickup"
                        ? "bg-clayBg text-clay border-clayBorder"
                        : "bg-oxblood/10 text-oxblood border-oxblood/40"
                    }`}
                    data-testid="admin-view-order-fulfillment-badge"
                  >
                    {viewingOrder.local_delivery_mode === "pickup" ? "Pickup" : "Local delivery"}
                  </span>
                )}
              </div>

              {/* Customer + shipping */}
              <div className="grid sm:grid-cols-2 gap-6 pt-4 border-t border-borderSubtle">
                <div>
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-2">Customer</p>
                  <p className="font-medium">{viewingOrder.customer_name || "—"}</p>
                  <a href={`mailto:${viewingOrder.customer_email}`} className="text-[13px] text-charcoal/75 hover:text-oxblood font-mono block break-all">{viewingOrder.customer_email}</a>
                  {viewingOrder.address?.phone && (
                    <p className="text-[13px] text-charcoal/75 font-mono">{viewingOrder.address.phone}</p>
                  )}
                </div>
                <div>
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-2">
                    {viewingOrder.local_delivery_mode === "pickup"
                      ? "Pickup — customer address"
                      : viewingOrder.local_delivery
                        ? "Local drop-off"
                        : "Ship to"}
                  </p>
                  {viewingOrder.address ? (
                    <div className="text-[15px] leading-relaxed">
                      {viewingOrder.address.name && <p>{viewingOrder.address.name}</p>}
                      {viewingOrder.address.line1 && <p>{viewingOrder.address.line1}</p>}
                      {viewingOrder.address.line2 && <p>{viewingOrder.address.line2}</p>}
                      {(viewingOrder.address.city || viewingOrder.address.state || viewingOrder.address.postal_code) && (
                        <p>
                          {[viewingOrder.address.city, viewingOrder.address.state].filter(Boolean).join(", ")}
                          {viewingOrder.address.postal_code ? ` ${viewingOrder.address.postal_code}` : ""}
                        </p>
                      )}
                      {viewingOrder.address.country && <p>{viewingOrder.address.country}</p>}
                    </div>
                  ) : (
                    <p className="text-[13px] text-charcoal/75 italic">No address on file</p>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="pt-4 border-t border-borderSubtle">
                <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-3">Items</p>
                <ul className="space-y-2" data-testid="admin-view-order-items">
                  {(viewingOrder.items || []).map((i, idx) => (
                    <li key={idx} className="flex items-start justify-between gap-4 pb-2 border-b border-borderSubtle/60 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{formatItemDisplay(i)}</p>
                        {i.sku && <p className="text-[13px] text-charcoal/75 font-mono">{i.sku}</p>}
                      </div>
                      <p className="font-mono text-[15px] shrink-0">× {i.quantity}</p>
                      <p className="font-mono text-[15px] text-oxblood shrink-0 w-20 text-right">${(Number(i.price || 0) * Number(i.quantity || 1)).toFixed(2)}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Totals */}
              <div className="pt-4 border-t border-borderSubtle">
                <dl className="space-y-1 font-mono text-[15px] max-w-xs ml-auto">
                  <div className="flex justify-between"><dt className="text-charcoal/75">Subtotal</dt><dd>${Number(viewingOrder.subtotal || 0).toFixed(2)}</dd></div>
                  {Number(viewingOrder.discount || 0) > 0 && (
                    <div className="flex justify-between"><dt className="text-charcoal/75">Discount{viewingOrder.discount_code ? ` (${viewingOrder.discount_code})` : ""}</dt><dd className="text-oxblood">-${Number(viewingOrder.discount).toFixed(2)}</dd></div>
                  )}
                  <div className="flex justify-between"><dt className="text-charcoal/75">Shipping</dt><dd>${Number(viewingOrder.shipping || 0).toFixed(2)}</dd></div>
                  <div className="flex justify-between pt-1 mt-1 border-t border-borderSubtle text-oxblood font-semibold text-[17px]">
                    <dt>Total</dt><dd>${Number(viewingOrder.total || 0).toFixed(2)}</dd>
                  </div>
                  {Number(viewingOrder.refunded_amount || 0) > 0 && (
                    <div className="flex justify-between pt-1 text-[13px] text-charcoal/75"><dt>Refunded</dt><dd>-${Number(viewingOrder.refunded_amount).toFixed(2)}</dd></div>
                  )}
                </dl>
              </div>

              {/* Tracking */}
              {viewingOrder.tracking_number && (
                <div className="pt-4 border-t border-borderSubtle">
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-2">Tracking</p>
                  <p className="font-mono text-[15px]">
                    {viewingOrder.tracking_carrier && <span className="uppercase text-[13px] mr-2">{viewingOrder.tracking_carrier}</span>}
                    {viewingOrder.tracking_number}
                  </p>
                </div>
              )}

              {/* Note to buyer */}
              {viewingOrder.note_to_buyer && (
                <div className="pt-4 border-t border-borderSubtle">
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-2">Note to buyer</p>
                  <p className="text-[15px] whitespace-pre-wrap bg-white border border-borderSubtle rounded p-3">{viewingOrder.note_to_buyer}</p>
                </div>
              )}

              {/* Status history */}
              {Array.isArray(viewingOrder.status_history) && viewingOrder.status_history.length > 0 && (
                <div className="pt-4 border-t border-borderSubtle">
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-2">Status history</p>
                  <ul className="space-y-1 text-[13px] text-charcoal/75 font-mono">
                    {viewingOrder.status_history.map((h, idx) => (
                      <li key={idx}>
                        <span className="uppercase">{h.status}</span>
                        {h.at && <span className="ml-2">· {new Date(h.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* iter156.4 — Buyer's pickup plan (only surfaced on pickup orders).
                  Clay callout aligns with Phase Purdy Pass 1 semantic pending/warning tone. */}
              {viewingOrder.local_delivery_mode === "pickup" && viewingOrder.pickup_notes && (
                <div className="pt-4 border-t border-borderSubtle">
                  <p className="text-[12px] uppercase tracking-[0.22em] text-clay font-mono font-semibold mb-2">Buyer's pickup plan</p>
                  <p
                    className="text-[15px] whitespace-pre-wrap bg-clayBg border-l-4 border-clay rounded p-4 italic text-charcoal leading-relaxed"
                    data-testid="admin-view-order-pickup-notes"
                  >
                    {viewingOrder.pickup_notes}
                  </p>
                </div>
              )}

              {/* Research-use acknowledgement audit — every storefront order
                  captures the buyer's explicit "I understand" checkbox click. */}
              {viewingOrder.research_ack && (
                <div className="pt-4 border-t border-borderSubtle">
                  <p className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 font-mono font-semibold mb-2">Research-use acknowledgement</p>
                  <p className="text-[13px] text-charcoal font-mono" data-testid="admin-view-order-research-ack">
                    ✓ Confirmed at checkout
                    {viewingOrder.research_ack_at && <span className="text-charcoal/75"> · {new Date(viewingOrder.research_ack_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t border-borderSubtle flex flex-wrap gap-2 justify-end">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${viewingOrder.id}/slip.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-borderSubtle hover:border-charcoal text-charcoal rounded-full text-[13px] uppercase tracking-[0.22em] transition-colors"
                  data-testid="admin-view-order-slip"
                >
                  <Printer size={13}/> Packing slip
                </a>
                <a
                  href={`mailto:${viewingOrder.customer_email}?subject=Order%20${viewingOrder.order_number}`}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-borderSubtle hover:border-charcoal text-charcoal rounded-full text-[13px] uppercase tracking-[0.22em] transition-colors"
                  data-testid="admin-view-order-email"
                >
                  <Mail size={13}/> Email buyer
                </a>
                <button
                  onClick={() => setViewingOrder(null)}
                  className="py-2 px-5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors"
                  data-testid="admin-view-order-close"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!noteOrderId} onOpenChange={(o) => { if (!o) closeOrderNote(); }}>
        <DialogContent className="rounded-2xl max-w-lg bg-bone border-borderSubtle" data-testid="admin-order-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Note to buyer</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-charcoal/75 -mt-2 mb-1">
            This note appears on the buyer's packing slip (both on-screen and PDF) and inside every status-change email. Keep it warm and specific.
          </p>
          {snippets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1" data-testid="admin-note-snippet-chips">
              <span className="overline text-[12px] text-charcoal/75 self-center mr-1">Insert</span>
              {snippets.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => insertSnippet(s.body)}
                  className="text-[12px] uppercase tracking-[0.16em] px-2.5 py-1 border border-borderSubtle hover:border-oxblood hover:text-oxblood rounded-full text-charcoal transition-colors"
                  title={s.body}
                  data-testid={`snippet-chip-${s.id}`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          )}
          <textarea
            value={noteOrderDraft}
            onChange={(e) => setNoteOrderDraft(e.target.value)}
            maxLength={2000}
            rows={5}
            placeholder="Thanks for your order — vials packed with ice. Please refrigerate on arrival."
            className="w-full text-sm bg-white border border-borderSubtle rounded p-3 focus:outline-none focus:border-charcoal"
            data-testid="admin-order-note-input"
            autoFocus
          />
          <p className="text-[13px] text-charcoal/75 text-right">{noteOrderDraft.length}/2000</p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={closeOrderNote}
              className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal"
              data-testid="admin-order-note-cancel"
            >
              Cancel
            </button>
            <button
              onClick={saveOrderNote}
              className="py-2.5 px-6 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors"
              data-testid="admin-order-note-save"
            >
              Save note
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking dialog */}
      <Dialog open={!!trackingOrderId} onOpenChange={(o) => { if (!o) closeTracking(); }}>
        <DialogContent className="rounded-2xl max-w-md bg-bone border-borderSubtle" data-testid="admin-tracking-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Tracking</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-charcoal/75 -mt-2 mb-2">
            Buyers see this on their order detail page. When you save a new tracking number, we auto-email them with a direct link to the carrier.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="overline font-bold text-charcoal">Carrier</Label>
              <Select value={trackingDraft.carrier || "none"} onValueChange={(v) => setTrackingDraft({ ...trackingDraft, carrier: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-2 rounded-none border-borderSubtle bg-white focus:ring-0" data-testid="tracking-carrier-select">
                  <SelectValue placeholder="Select carrier"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline font-bold text-charcoal">Tracking number</Label>
              <Input
                value={trackingDraft.number}
                onChange={(e) => setTrackingDraft({ ...trackingDraft, number: e.target.value })}
                className="mt-2 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                placeholder="e.g. 9400 1000 0000 0000 0000 00"
                maxLength={80}
                data-testid="tracking-number-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <button
              onClick={closeTracking}
              className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal"
              data-testid="admin-tracking-cancel"
            >
              Cancel
            </button>
            <button
              onClick={saveTracking}
              className="py-2.5 px-6 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors"
              data-testid="admin-tracking-save"
            >
              Save tracking
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund dialog — supports full + partial refunds */}
      <Dialog open={!!refundOrderId} onOpenChange={(o) => { if (!o) closeRefund(); }}>
        <DialogContent className="rounded-2xl max-w-md bg-bone border-borderSubtle" data-testid="admin-refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Refund</DialogTitle>
          </DialogHeader>
          {(() => {
            const o = orders.find((x) => x.id === refundOrderId);
            if (!o) return null;
            const alreadyRefunded = o.refunded_amount || 0;
            const remaining = o.total - alreadyRefunded;
            return (
              <>
                <p className="text-[13px] text-charcoal/75 -mt-2 mb-3">
                  Order <span className="font-mono text-charcoal">#{o.order_number}</span> · Total <span className="font-mono text-charcoal">${o.total.toFixed(2)}</span>
                  {alreadyRefunded > 0 && <> · Already refunded <span className="font-mono text-oxblood">${alreadyRefunded.toFixed(2)}</span></>}
                </p>
                <div>
                  <Label className="overline font-bold text-charcoal">Amount to refund</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={remaining.toFixed(2)}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="pl-7 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood font-mono"
                      data-testid="refund-amount-input"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button type="button" onClick={() => setRefundAmount(remaining.toFixed(2))} className="text-[12px] uppercase tracking-[0.18em] px-2.5 py-1 border border-borderSubtle hover:border-oxblood hover:text-oxblood rounded-full" data-testid="refund-full-btn">Full · ${remaining.toFixed(2)}</button>
                    <button type="button" onClick={() => setRefundAmount((remaining / 2).toFixed(2))} className="text-[12px] uppercase tracking-[0.18em] px-2.5 py-1 border border-borderSubtle hover:border-oxblood hover:text-oxblood rounded-full">Half</button>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-2">Max refundable: <span className="font-mono">${remaining.toFixed(2)}</span></p>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button onClick={closeRefund} className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal" data-testid="admin-refund-cancel">Cancel</button>
                  <button onClick={submitRefund} disabled={refundBusy} className="py-2.5 px-6 bg-oxblood hover:bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50" data-testid="admin-refund-submit">
                    {refundBusy ? "Refunding…" : `Refund $${parseFloat(refundAmount || 0).toFixed(2)}`}
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Restock waitlist per product */}
      <Dialog open={!!waitlistProduct} onOpenChange={(o) => { if (!o) { setWaitlistProduct(null); setWaitlistEntries([]); } }}>
        <DialogContent className="rounded-2xl max-w-lg bg-bone border-borderSubtle max-h-[80vh] overflow-y-auto" data-testid="waitlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Restock waitlist
              {waitlistProduct && <span className="block text-sm text-charcoal/75 font-sans mt-1">{waitlistProduct.name}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[13px] text-charcoal/75">
              When you flip this kit back to <span className="text-charcoal">In stock</span>, everyone below is emailed instantly and the list auto-clears.
            </p>
            {waitlistLoading && <p className="overline text-charcoal/75">Loading…</p>}
            {!waitlistLoading && waitlistEntries.length === 0 && (
              <p className="text-sm text-charcoal/75">No one waiting yet.</p>
            )}
            {!waitlistLoading && waitlistEntries.length > 0 && (
              <ul className="divide-y divide-borderSubtle bg-white rounded-xl border border-borderSubtle overflow-hidden">
                {waitlistEntries.map((e) => (
                  <li key={e.id} className="px-4 py-3 flex justify-between items-center gap-3" data-testid={`waitlist-row-${e.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm truncate">{e.name || "—"}</p>
                      <p className="text-[13px] font-mono text-charcoal/75 truncate">{e.email}</p>
                    </div>
                    <p className="text-[12px] uppercase tracking-widest text-charcoal/75 shrink-0">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete customer confirmation */}
      <Dialog open={!!confirmDeleteCustomer} onOpenChange={(o) => { if (!o) setConfirmDeleteCustomer(null); }}>
        <DialogContent className="rounded-2xl max-w-md bg-bone border-borderSubtle" data-testid="confirm-delete-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Delete this customer?</DialogTitle>
          </DialogHeader>
          {confirmDeleteCustomer && (
            <div className="text-sm text-charcoal space-y-4">
              <p>
                You're about to hard-delete <span className="font-mono text-oxblood">{confirmDeleteCustomer.email}</span> and
                everything tied to their account — notifications, subscriptions, waitlist entries, cart, saved addresses.
              </p>
              <div className="border border-borderSubtle bg-white p-4 rounded-xl">
                <p className="overline text-charcoal/75 mb-1">Their {confirmDeleteCustomer.orders_count} order{confirmDeleteCustomer.orders_count === 1 ? "" : "s"} will be preserved</p>
                <p className="text-[13px] text-charcoal/75">Order history stays in the Orders tab (financial record). Rows will show <span className="font-mono">customer_deleted</span>.</p>
              </div>
              <p className="text-[13px] text-charcoal/75">This cannot be undone.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setConfirmDeleteCustomer(null)}
                  className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal"
                  data-testid="cancel-delete-customer-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteCustomer(confirmDeleteCustomer)}
                  disabled={customerBusyId === confirmDeleteCustomer.id}
                  className="py-2.5 px-6 bg-oxblood hover:bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-customer-btn"
                >
                  {customerBusyId === confirmDeleteCustomer.id ? "Deleting…" : "Delete customer"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
      </div>
    </div>
  );
}
